import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Card, Modal, ConfirmDialog, FormField, TextInput, TextArea, FormSelect,
  StatusBadge, Drawer, Toggle, PageHeader, DataTable, FilterBar, Tabs,
  EmptyState, Icon, type DataTableColumn, type Tone,
} from '@/components/ui';
import { superApi, type VoiceAgentRow, type VoiceDirection, type VoiceStatus, type VoiceToolCatalogItem, type VoiceCustomTool, type VoiceAssignment, type VoiceSession, type VoiceMemory, type VoiceTool, type VoiceFlowRow, type VoiceAgentNumber, type VoiceLiveCall, type VoiceAgentExtension, type ElevenLabsCatalog, type ElevenLabsV3TagGroup, type VoicePaymentCredential, type VoiceLiveModel,
  type OpenAiVoice, type OpenAiCatalog, type ElevenLabsHostedAgent } from '@/shared/api/superApi';
import { toast } from '@/components/ui/Toast';
import { AudioPlayer } from '@/components/ui/AudioPlayer';
import { Wizard } from '@/components/ui';
import { FlowBuilder, FlowMetaForm } from './VoiceFlows';
import { StudioRecordsTab } from './StudioRecordsTab';
import { StudioStatsTab } from './StudioStatsTab';
import { StudioKnowledgeTab } from './StudioKnowledgeTab';
import { VoiceToolForm } from './VoiceTools';

// ── Display maps ────────────────────────────────────────────────────────────
const STATUS_TONE: Record<VoiceStatus, Tone> = { active: 'ok', inactive: 'neutral', draft: 'warn' };
const STATUS_LABEL: Record<VoiceStatus, string> = { active: 'פעיל', inactive: 'כבוי', draft: 'טיוטה' };
const DIRECTION_LABEL: Record<VoiceDirection, string> = { inbound: 'נכנס', outbound: 'יוצא', both: 'דו-כיווני' };

/** סוכן שכל השיחה שלו מתנהלת אצל ElevenLabs ולא בגשר שלנו. */
const isHostedRow = (r: { settings?: Record<string, unknown> | null }) =>
  (r.settings as { agent_host?: string } | null | undefined)?.agent_host === 'elevenlabs';

/**
 * מה לא עובד בסוכן מתארח, ולמה. מוצג למשתמש כדי שהחסימה לא תיראה כתקלה.
 * זרימה/כלים/רשומות/מאגר-ידע מנוהלים אצלם; העברה לנציג טרם מומשה.
 */
const HOSTED_UNAVAILABLE: { label: string; why: string }[] = [
  { label: 'זרימת שיחה', why: 'הזרימה מנוהלת אצל ElevenLabs, לא במנוע שלנו' },
  { label: 'כלי API ורשומות', why: 'הכלים נדחפים אליהם אוטומטית בסנכרון' },
  { label: 'מאגר ידע', why: 'ה-RAG שלנו לא נכנס לשיחה שרצה אצלם' },
  { label: 'שיחה חיה', why: 'אין לנו רגל מדיה בשיחה — אין מה לנטר בזמן אמת' },
  { label: 'העברה לנציג', why: 'טרם מומש: דורש מיפוי יעדי העברה לפורמט שלהם' },
];

interface TenantLite { id: number; name: string; slug: string }

interface TransferTargetDraft { key: string; label?: string; type: 'extension' | 'external'; target: string; hours_gated?: boolean; when?: Record<string, unknown> | null }

interface HoursDayDraft { day: number; enabled: boolean; open: string; close: string }

const DAY_LABEL = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

/**
 * מנרמל את settings.hours.custom לשבעה ימים מלאים. השרת שומר רק את הימים
 * שהוגדרו, ובלי ההשלמה הזו יום חסר היה נעלם מהטופס ונמחק בשמירה הבאה.
 */
function mergeHoursDays(saved?: Partial<HoursDayDraft>[] | null): HoursDayDraft[] {
  return Array.from({ length: 7 }, (_, day) => {
    const row = (saved ?? []).find((d) => Number(d?.day) === day);
    return {
      day,
      enabled: row ? row.enabled !== false : false,
      open: row?.open || '09:00',
      close: row?.close || '17:00',
    };
  });
}

// ── Page ────────────────────────────────────────────────────────────────────
export function VoiceAgents() {
  const [rows, setRows] = useState<VoiceAgentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [directionFilter, setDirectionFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [fullFormOpen, setFullFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<VoiceAgentRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<VoiceAgentRow | null>(null);
  const [drawerId, setDrawerId] = useState<number | null>(null);
  const [studioNonce, setStudioNonce] = useState(0);

  const [tools, setTools] = useState<VoiceToolCatalogItem[]>([]);
  const [libraryTools, setLibraryTools] = useState<VoiceTool[]>([]);
  const [flows, setFlows] = useState<VoiceFlowRow[]>([]);
  const [tenants, setTenants] = useState<TenantLite[]>([]);

  // Monotonic request id: typing fires a fetch per keystroke; only the latest
  // response is allowed to win, so a slow earlier query can't clobber the table.
  const reqSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++reqSeq.current;
    setLoading(true);
    try {
      const res = await superApi.voiceAgents({
        search: search || undefined,
        status: statusFilter || undefined,
        direction: directionFilter || undefined,
        per_page: 100,
      });
      if (seq !== reqSeq.current) return; // a newer request superseded this one
      setRows(res.data);
    } catch {
      if (seq === reqSeq.current) toast('שגיאה בטעינת סוכני הקול', 'danger');
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, [search, statusFilter, directionFilter]);

  // Debounce so a search doesn't fire one request per character.
  useEffect(() => {
    const h = setTimeout(() => void load(), 250);
    return () => clearTimeout(h);
  }, [load]);

  // Catalog + tenants list are static enough to load once (וגם נטענים מחדש
  // אחרי יצירת כלי/זרימה מתוך הסטודיו).
  const loadMeta = useCallback(async () => {
      try { setTools(await superApi.voiceTools()); } catch { /* non-fatal */ }
      try { setLibraryTools((await superApi.voiceToolsLibrary({ per_page: 200 })).data); } catch { /* non-fatal */ }
      try { setFlows((await superApi.voiceFlows({ per_page: 200 })).data); } catch { /* non-fatal */ }
  }, []);
  useEffect(() => {
    void (async () => {
      await loadMeta();
      try {
        const t = await superApi.tenants({ per_page: 500 });
        const items = (t as { data?: unknown[] }).data ?? [];
        setTenants(items.map((x) => x as TenantLite).filter((x) => x && typeof x.id === 'number'));
      } catch { /* non-fatal */ }
    })();
  }, [loadMeta]);

  const tenantName = useCallback(
    (id?: number | null) => (id == null ? null : tenants.find((t) => t.id === id)?.name ?? `#${id}`),
    [tenants],
  );

  const columns: DataTableColumn<VoiceAgentRow>[] = useMemo(() => [
    { key: 'name', he: 'שם', render: (r) => <strong>{r.name}</strong> },
    {
      key: 'scope', he: 'שיוך', render: (r) =>
        r.tenant_id
          ? <StatusBadge tone="neutral" size="sm">{tenantName(r.tenant_id)}</StatusBadge>
          : <StatusBadge tone="ok" size="sm">גלובלי · {r.assignments_count ?? 0} מרכזיות</StatusBadge>,
    },
    { key: 'direction', he: 'כיוון', render: (r) => DIRECTION_LABEL[r.direction] ?? r.direction },
    {
      // סוכן מתארח לא רץ על מודל השיחה שלנו — הצגת gemini_model עבורו
      // הייתה משקרת. במקומו תג שאומר איפה השיחה באמת מתנהלת.
      key: 'model', he: 'מודל', align: 'start',
      render: (r) => isHostedRow(r)
        ? <StatusBadge tone="warn" size="sm">☁ ElevenLabs</StatusBadge>
        : <span dir="ltr" style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{r.gemini_model ?? '—'}</span>,
    },
    { key: 'calls_count', he: 'שיחות', align: 'center', render: (r) => r.calls_count ?? 0 },
    {
      key: 'status', he: 'סטטוס', align: 'center',
      render: (r) => <StatusBadge tone={STATUS_TONE[r.status] ?? 'neutral'} dot size="sm">{STATUS_LABEL[r.status] ?? r.status}</StatusBadge>,
    },
  ], [tenantName]);

  async function handleDelete(agent: VoiceAgentRow) {
    try {
      await superApi.deleteVoiceAgent(agent.id);
      toast('הסוכן נמחק', 'ok');
      setDeleteTarget(null);
      if (drawerId === agent.id) setDrawerId(null);
      setRows((prev) => prev.filter((r) => r.id !== agent.id));
    } catch {
      toast('שגיאה במחיקה', 'danger');
    }
  }

  // מצב סטודיו: סוכן פתוח = מסך מלא (זרימה מוטמעת, כלים, מספרים, debug).
  if (drawerId != null) {
    return (
      <>
        <VoiceAgentStudio
          agentId={drawerId}
          nonce={studioNonce}
          tools={tools}
          libraryTools={libraryTools}
          flows={flows}
          tenants={tenants}
          tenantName={tenantName}
          onClose={() => { setDrawerId(null); void load(); }}
          onEdit={(a) => setEditTarget(a)}
          onDelete={(a) => setDeleteTarget(a)}
          onMetaChanged={() => void loadMeta()}
          onAgentPatched={() => setStudioNonce((n) => n + 1)}
        />
        {editTarget && (
          <VoiceAgentForm
            mode="edit"
            agent={editTarget}
            tools={tools}
            libraryTools={libraryTools}
            flows={flows}
            tenants={tenants}
            onClose={() => setEditTarget(null)}
            onSaved={() => { setEditTarget(null); setStudioNonce((n) => n + 1); void load(); }}
          />
        )}
        <ConfirmDialog
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => deleteTarget && handleDelete(deleteTarget)}
          title="מחיקת סוכן קול"
          message={deleteTarget ? `למחוק את "${deleteTarget.name}"? שיחות שמנותבות אליו יפסיקו לעבוד, וכל הזיכרון והתמלילים יימחקו.` : ''}
          confirmLabel="מחק"
        />
      </>
    );
  }

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <PageHeader
        title="סוכני קול AI"
        subtitle="סוכנים בזמן אמת (Gemini Live) שמדברים בטלפון דרך התשתית"
        actions={
          <button type="button" className="btn primary" onClick={() => setCreateOpen(true)}>
            + סוכן חדש
          </button>
        }
      />

      <FilterBar
        search={search}
        onSearch={setSearch}
        onClear={() => setSearch('')}
        placeholder="חיפוש לפי שם…"
      >
        <FormSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 160, height: 36 }}>
          <option value="">כל הסטטוסים</option>
          <option value="active">פעיל</option>
          <option value="inactive">כבוי</option>
          <option value="draft">טיוטה</option>
        </FormSelect>
        <FormSelect value={directionFilter} onChange={(e) => setDirectionFilter(e.target.value)} style={{ width: 160, height: 36 }}>
          <option value="">כל הכיוונים</option>
          <option value="inbound">נכנס</option>
          <option value="outbound">יוצא</option>
          <option value="both">דו-כיווני</option>
        </FormSelect>
      </FilterBar>

      <Card>
        <DataTable
          columns={columns}
          rows={rows}
          loading={loading}
          onRowClick={(r) => setDrawerId(r.id)}
          caption="סוכני קול"
          emptyState={
            <EmptyState
              title="אין סוכני קול עדיין" description="צור סוכן ראשון כדי שיענה לשיחות דרך המרכזיה."
              action={<button type="button" className="btn primary" onClick={() => setCreateOpen(true)}>+ סוכן חדש</button>}
            />
          }
        />
      </Card>

      {createOpen && (
        <VoiceAgentWizard
          libraryTools={libraryTools}
          flows={flows}
          tenants={tenants}
          onClose={() => setCreateOpen(false)}
          onFullForm={() => { setCreateOpen(false); setFullFormOpen(true); }}
          onCreated={(id) => { setCreateOpen(false); void load(); setDrawerId(id); }}
        />
      )}

      {fullFormOpen && (
        <VoiceAgentForm
          mode="create"
          tools={tools}
          libraryTools={libraryTools}
          flows={flows}
          tenants={tenants}
          onClose={() => setFullFormOpen(false)}
          onSaved={() => { setFullFormOpen(false); void load(); }}
        />
      )}

      {editTarget && (
        <VoiceAgentForm
          mode="edit"
          agent={editTarget}
          tools={tools}
          libraryTools={libraryTools}
          flows={flows}
          tenants={tenants}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); void load(); }}
        />
      )}

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && handleDelete(deleteTarget)}
        title="מחיקת סוכן קול"
        message={deleteTarget ? `למחוק את "${deleteTarget.name}"? שיחות שמנותבות אליו יפסיקו לעבוד, וכל הזיכרון והתמלילים יימחקו.` : ''}
        confirmLabel="מחק"
      />

    </div>
  );
}

// ── Create / edit form ───────────────────────────────────────────────────────
// ריק = "ברירת המחדל של השרת". בכוונה לא מזהה קשיח: המזהה הישן
// 'gemini-live-2.5-flash-native-audio' אינו קיים ומחזיר 1008 (שיחה אילמת),
// וה-allow-list המאומת מגיע מ-GET /super/voice-agents/models.
const DEFAULT_MODEL = '';

const ENGINE_LABEL: Record<string, string> = { gemini: 'Gemini Live', openai: 'OpenAI Realtime' };

/**
 * מפתחות שנשמרים ב-settings (JSON) אך נשלחים שטוחים ב-payload — ולכן
 * ההשוואה "לפני" צריכה לקרוא מ-agent.settings ולא מ-agent עצמו.
 */
const SETTINGS_KEYS = new Set([
  'agent_host', 'elevenlabs_agent_id', 'speech_speed',
  'voice_provider', 'elevenlabs_voice_id', 'elevenlabs_model_id', 'elevenlabs_stability',
  'elevenlabs_humanize', 'elevenlabs_tags', 'elevenlabs_similarity', 'elevenlabs_speaker_boost',
  'vad_start_sensitivity', 'vad_end_sensitivity', 'vad_prefix_ms', 'vad_silence_ms',
  'address_style', 'pronunciation_map', 'webhook', 'qa_scoring', 'hours', 'after_hours',
  'transfer_targets', 'noise_profile', 'payment',
]);

/**
 * משאיר רק שדות שבאמת השתנו מול הסוכן השמור.
 *
 * הטופס בונה תמיד את **כל** השדות, ולכן שמירה אחת דרסה כוונון שנעשה מחוץ
 * למסך (MCP/API): פרומפט של 12,299 תווים חזר ל-1,982, מודל השיחה חזר
 * מ-gpt-realtime-2.1 ל-Gemini, ו-greeting_text התאפס ל-NULL — הכל משמירה
 * אחת שלא נגעה בשדות האלה. שליחת דלתא בלבד סוגרת את זה: מה שלא נגעת בו
 * לא נשלח, ולכן לא יכול לדרוס.
 *
 * `undefined` מסונן ממילא (שדות ElevenLabs כשהמנוע אחר) — הוא "אל תשלח",
 * בשונה מ-`null` שהוא "נקה את הערך".
 */
/**
 * סקשן בטופס העריכה: כותרת לחיצה שמקפלת את התוכן.
 *
 * הטופס מחזיק 52 שדות; ברצף פתוח אחד המשתמש נאלץ לגלול מאות שורות כדי
 * למצוא שדה בודד. הקיפול משאיר את המפה גלויה, וה-anchor מאפשר קפיצה
 * ישירה מהניווט למעלה.
 */
function FormSection({ id, title, subtitle, badge, defaultOpen = true, children }: {
  id: string;
  title: string;
  subtitle?: string;
  /** מונה/סטטוס קצר שנשאר גלוי גם כשהסקשן מקופל. */
  badge?: string | null;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <section id={id} style={{ borderTop: '1px solid var(--line)', paddingTop: 8, marginTop: 4, scrollMarginTop: 56 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center',
          gap: 8, width: '100%', padding: '2px 0',
        }}
      >
        <span aria-hidden style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>▶</span>
        <strong style={{ fontSize: 'var(--t-sm)' }}>{title}</strong>
        {badge && <StatusBadge tone="neutral" size="sm">{badge}</StatusBadge>}
        {subtitle && !open && (
          <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {subtitle}
          </span>
        )}
      </button>
      {open && <div className="stack" style={{ marginTop: 8 }}>{children}</div>}
    </section>
  );
}

/** ניווט דביק: קפיצה לסקשן בלי לגלול את כל הטופס. */
function FormSectionNav({ sections }: { sections: { id: string; label: string }[] }) {
  return (
    <nav
      aria-label="מעבר לחלק בטופס"
      style={{
        position: 'sticky', top: 0, zIndex: 2, display: 'flex', gap: 4,
        flexWrap: 'wrap', padding: '6px 0 8px', marginBottom: 4,
        background: 'var(--surface)', borderBottom: '1px solid var(--line)',
      }}
    >
      {sections.map((s) => (
        <button
          key={s.id}
          type="button"
          className="btn ghost"
          style={{ fontSize: 'var(--t-xs)', padding: '2px 8px' }}
          onClick={() => document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
        >
          {s.label}
        </button>
      ))}
    </nav>
  );
}

function changedOnly(payload: Partial<VoiceAgentRow>, agent: VoiceAgentRow): Partial<VoiceAgentRow> {
  const settings = (agent.settings ?? {}) as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const [k, next] of Object.entries(payload)) {
    if (next === undefined) continue;
    const prev = SETTINGS_KEYS.has(k)
      ? settings[k]
      : (agent as unknown as Record<string, unknown>)[k];
    // השוואה עמוקה: מערכים/אובייקטים (כלים, יעדי העברה, שעות) נבנים מחדש
    // בכל render ולכן השוואת הפניה תסמן אותם כשונים תמיד.
    if (JSON.stringify(next ?? null) !== JSON.stringify(prev ?? null)) out[k] = next;
  }

  return out as Partial<VoiceAgentRow>;
}

/**
 * פאנל סוכן מתארח ב-ElevenLabs Agents.
 *
 * מקור האמת נשאר המרכזיה: עורכים כאן, ו"סנכרן" דוחף את הפרומפט המלא
 * (כולל בלוק הניקוד), הקול וה-LLM אליהם. אפשר גם לאמץ סוכן שכבר נוצר
 * בקונסולה שלהם — כדי לא לכפות יצירה מחדש.
 */
function HostedElevenLabsPanel({ agentId, value, onChange, lastDeployedAt, enabledTools = [] }: {
  agentId: number | null;
  value: string;
  onChange: (v: string) => void;
  lastDeployedAt: string | null;
  /** הכלים שסומנו לסוכן — כדי להראות מה מהם באמת יגיע ל-ElevenLabs. */
  enabledTools?: string[];
}) {
  const [agents, setAgents] = useState<ElevenLabsHostedAgent[]>([]);
  const [configured, setConfigured] = useState(true);
  const [busy, setBusy] = useState<'deploy' | 'sync' | null>(null);
  const [deployedAt, setDeployedAt] = useState(lastDeployedAt);

  useEffect(() => {
    void superApi.elevenlabsAgents()
      .then((r) => { setAgents(r.agents); setConfigured(r.configured); })
      .catch(() => setConfigured(false));
  }, []);

  async function deploy() {
    if (!agentId) { toast('שמור את הסוכן קודם', 'danger'); return; }
    setBusy('deploy');
    try {
      const r = await superApi.elevenlabsDeploy(agentId);
      onChange(r.elevenlabs_agent_id);
      setDeployedAt(r.last_deployed_at);
      toast(r.created ? 'נוצר סוכן חדש ב-ElevenLabs' : 'ההגדרות נדחפו', 'ok');
    } catch { toast('הדחיפה נכשלה', 'danger'); } finally { setBusy(null); }
  }

  async function sync() {
    if (!agentId) return;
    setBusy('sync');
    try {
      const r = await superApi.elevenlabsSync(agentId);
      toast(r.imported ? `יובאו ${r.imported} שיחות` : 'אין שיחות חדשות', 'ok');
    } catch { toast('הסנכרון נכשל', 'danger'); } finally { setBusy(null); }
  }

  if (!configured) {
    return (
      <p style={{ margin: 0, fontSize: 'var(--t-sm)', color: 'var(--danger)' }}>
        לא הוגדר מפתח ElevenLabs — לא ניתן לנהל סוכן מתארח.
      </p>
    );
  }

  return (
    <div className="stack" style={{ gap: 8, padding: 'var(--pad-card)', border: '1px solid var(--line)', borderRadius: 'var(--r-2)' }}>
      <FormField label="הסוכן ב-ElevenLabs" hint="ריק = ייווצר סוכן חדש בדחיפה הראשונה.">
        <FormSelect value={value} dir="ltr" onChange={(e) => onChange(e.target.value)}>
          <option value="">— צור חדש בדחיפה —</option>
          {agents.map((a) => (
            <option key={a.agent_id} value={a.agent_id} disabled={!!a.linked_to && a.agent_id !== value}>
              {a.name}{a.linked_to && a.agent_id !== value ? ` (משויך ל-${a.linked_to.name})` : ''}
            </option>
          ))}
        </FormSelect>
      </FormField>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" className="btn" disabled={busy !== null} onClick={() => void deploy()}>
          {busy === 'deploy' ? '⏳ דוחף…' : '⬆ סנכרן ל-ElevenLabs'}
        </button>
        <button type="button" className="btn ghost" disabled={busy !== null || !value} onClick={() => void sync()}>
          {busy === 'sync' ? '⏳ מייבא…' : '⬇ ייבא שיחות'}
        </button>
        {!deployedAt && (
          <span style={{ fontSize: 'var(--t-xs)', color: 'var(--warn, var(--muted))' }}>
            ⚠ טרם סונכרן — השינויים כאן עדיין לא הגיעו ל-ElevenLabs
          </span>
        )}
        {deployedAt && (
          <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }} dir="ltr">
            סונכרן: {new Date(deployedAt).toLocaleString('he-IL')}
          </span>
        )}
      </div>
      {enabledTools.length > 0 && (
        <div style={{ fontSize: 'var(--t-xs)' }}>
          <div style={{ fontWeight: 600, marginBottom: 3 }}>הכלים בדחיפה:</div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {enabledTools.map((t) => {
              // end_call קיים אצלם כ-built_in; transfer_to_human דורש מיפוי
              // יעדים שטרם מומש ולכן אינו נדחף כלל — עדיף לומר את זה מראש.
              const mode = t === 'end_call' ? 'built-in'
                : t === 'transfer_to_human' ? 'לא נתמך' : 'webhook';
              return (
                <StatusBadge key={t} size="sm" tone={mode === 'לא נתמך' ? 'danger' : mode === 'built-in' ? 'neutral' : 'ok'}>
                  <span dir="ltr">{t}</span> · {mode}
                </StatusBadge>
              );
            })}
          </div>
        </div>
      )}
      <p style={{ margin: 0, fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
        הדחיפה שולחת את ההנחיות המלאות (כולל ניקוד), הברכה, הקול והכלים. שינויים שנעשו בקונסולה של ElevenLabs יידרסו.
      </p>
    </div>
  );
}

const GENDER_LABEL: Record<string, string> = { male: 'גברי', female: 'נשי', neutral: 'ניטרלי' };

/**
 * בורר קולות OpenAI עם השמעת דגימה.
 *
 * למה נגן ולא רק תווית: בדיקה אוטומטית לא הבחינה בין עשרת הקולות (כולם
 * מדברים עברית, כולם החזירו תמלול זהה, השהיה 594-840ms). איכות ההגייה
 * היא שיפוט אנושי — ולכן ההכרעה היא באוזן. הסימון "מומלץ" משקף את
 * המלצת OpenAI בתיעוד, לא מדידה שלנו, וזה נאמר במפורש.
 */
function OpenAiVoicePicker({ voices, value, onChange, id }: {
  voices: OpenAiVoice[]; value: string; onChange: (v: string) => void; id?: string;
}) {
  const [playing, setPlaying] = useState<string | null>(null);
  const [loading, setLoading] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlRef = useRef<string | null>(null);

  const stop = useCallback(() => {
    audioRef.current?.pause();
    audioRef.current = null;
    if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; }
  }, []);
  useEffect(() => stop, [stop]);

  async function preview(voice: string) {
    stop();
    if (playing === voice) { setPlaying(null); return; }
    setPlaying(null);
    setLoading(voice);
    try {
      // הסינתוז אורך כשנייה בפעם הראשונה לכל קול (אחר כך cache בשרת).
      const url = await superApi.openaiVoicePreview(voice);
      urlRef.current = url;
      const a = new Audio(url);
      a.onended = () => { setPlaying(null); stop(); };
      a.onerror = () => { setPlaying(null); stop(); toast('לא הצלחתי להשמיע דגימה', 'danger'); };
      audioRef.current = a;
      await a.play();
      setPlaying(voice);
    } catch {
      toast('לא הצלחתי להשמיע דגימה', 'danger');
      stop();
    } finally {
      setLoading(null);
    }
  }

  return (
    <div className="stack" style={{ gap: 6 }}>
      <FormSelect id={id} value={value} dir="ltr" onChange={(e) => onChange(e.target.value)}>
        {voices.map((v) => (
          <option key={v.id} value={v.id}>
            {v.label} — {GENDER_LABEL[v.gender] ?? v.gender}{v.recommended ? ' ★ מומלץ' : ''}
          </option>
        ))}
      </FormSelect>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {voices.map((v) => (
          <button
            key={v.id}
            type="button"
            className={v.id === value ? 'btn' : 'btn ghost'}
            style={{ fontSize: 'var(--t-xs)', padding: '2px 8px' }}
            title={v.note ?? `${v.label} — ${GENDER_LABEL[v.gender] ?? v.gender}`}
            disabled={loading !== null}
            onClick={() => { onChange(v.id); void preview(v.id); }}
          >
            {loading === v.id ? '⏳' : playing === v.id ? '⏸' : '▶'} {v.label}{v.recommended ? ' ★' : ''}
          </button>
        ))}
      </div>
      <p style={{ margin: 0, fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
        לחיצה בוחרת ומשמיעה דגימה בעברית. ★ = מומלץ ע"י OpenAI לאיכות — כל הקולות מדברים עברית, וההבדל ביניהם נשמע באוזן.
      </p>
    </div>
  );
}

/** שם המנוע שמודל השיחה רץ עליו — לתוויות שתלויות בו. */
function engineLabelFor(models: VoiceLiveModel[], modelId: string): string {
  const engine = models.find((m) => m.id === modelId)?.engine ?? 'gemini';
  return ENGINE_LABEL[engine] ?? engine;
}

/** הערת עזר מתחת לבורר: איזה מנוע נבחר, ואם המנוע חסר מפתח API. */
function modelHint(models: VoiceLiveModel[], id: string): string | undefined {
  if (!id) return 'ברירת המחדל של המערכת (מודל מאומת).';
  const m = models.find((x) => x.id === id);
  if (!m) return undefined;
  const engine = ENGINE_LABEL[m.engine ?? 'gemini'] ?? m.engine;
  return m.available === false
    ? `${engine} — חסר מפתח API, שיחה תיפול חזרה ל-Gemini.`
    : `מנוע: ${engine}`;
}

/**
 * בורר מודל מקובץ לפי מנוע. היה שדה טקסט חופשי, שבו טעות הקלדה אחת
 * גורמת ל-1008 NOT_FOUND ולשיחה אילמת — ולכן הרשימה נאכפת מהשרת.
 * ערך שנשמר בעבר ואינו ברשימה נשמר כאופציה נפרדת כדי לא להיעלם בשקט.
 */
function ModelSelect({ models, value, onChange, id }: {
  models: VoiceLiveModel[]; value: string; onChange: (v: string) => void;
  /** מוזרק ע"י FormField כדי לקשור את ה-label ל-select (WCAG 1.3.1). */
  id?: string;
}) {
  const groups: { engine: string; rows: VoiceLiveModel[] }[] = [];
  for (const m of models) {
    const engine = m.engine ?? 'gemini';
    let g = groups.find((x) => x.engine === engine);
    if (!g) { g = { engine, rows: [] }; groups.push(g); }
    g.rows.push(m);
  }
  const known = models.some((m) => m.id === value);

  return (
    <FormSelect id={id} value={value} dir="ltr" onChange={(e) => onChange(e.target.value)}>
      <option value="">— ברירת מחדל —</option>
      {groups.map((g) => (
        <optgroup key={g.engine} label={ENGINE_LABEL[g.engine] ?? g.engine}>
          {g.rows.map((m) => (
            <option key={m.id} value={m.id} disabled={m.available === false}>
              {m.label}{m.available === false ? ' (חסר מפתח)' : ''}
            </option>
          ))}
        </optgroup>
      ))}
      {!known && value && <option value={value}>{value} (לא ברשימה)</option>}
    </FormSelect>
  );
}

/** "מילה = הגייה" שורה-לשורה ↔ אובייקט מילון ההגייה של הסוכן. */
function parsePronunciationText(text: string): Record<string, string> | null {
  const map: Record<string, string> = {};
  for (const line of text.split('\n')) {
    const idx = line.indexOf('=');
    if (idx < 1) continue;
    const k = line.slice(0, idx).trim();
    const v = line.slice(idx + 1).trim();
    if (k && v) map[k] = v;
  }
  return Object.keys(map).length ? map : null;
}

/**
 * אשף "סוכן חדש" מודרך: שם+הנחיות ← קול ← זרימה ← כלים ← סיכום.
 * בסיום נוחתים בסטודיו של הסוכן להשלמת מספר/כיוונון. הטופס המלא נשאר
 * זמין דרך "מעבר לטופס המלא".
 */
function VoiceAgentWizard({ libraryTools, flows, tenants, onClose, onFullForm, onCreated }: {
  libraryTools: VoiceTool[];
  flows: VoiceFlowRow[];
  tenants: TenantLite[];
  onClose: () => void;
  onFullForm: () => void;
  onCreated: (id: number) => void;
}) {
  const [saving, setSaving] = useState(false);
  const [liveModels, setLiveModels] = useState<VoiceLiveModel[]>([]);
  useEffect(() => {
    void superApi.voiceModels().then((r) => setLiveModels(r.models)).catch(() => {});
  }, []);

  async function finish(data: Record<string, unknown>) {
    if (saving) return;
    setSaving(true);
    try {
      const created = await superApi.createVoiceAgent({
        name: String(data.name ?? '').trim(),
        tenant_id: data.tenant_id ? Number(data.tenant_id) : null,
        status: 'draft',
        direction: (data.direction as VoiceDirection) ?? 'inbound',
        system_prompt: String(data.system_prompt ?? ''),
        greeting_text: (data.greeting_text as string) || null,
        gemini_model: (data.gemini_model as string) || DEFAULT_MODEL,
        voice_provider: (data.voice_provider as string) ?? 'gemini',
        language: 'he-IL',
        memory_enabled: true,
        flow_id: data.flow_id ? Number(data.flow_id) : null,
        tool_ids: (data.tool_ids as number[]) ?? [],
      } as Partial<VoiceAgentRow>);
      toast('הסוכן נוצר — נחתנו בסטודיו להשלמת ההגדרות', 'ok');
      onCreated(created.id);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה ביצירת הסוכן';
      toast(msg, 'danger');
      setSaving(false);
    }
  }

  const steps = [
    {
      id: 'basics', title: 'שם והנחיות',
      validate: (d: Record<string, unknown>) => {
        if (!String(d.name ?? '').trim()) return 'יש למלא שם לסוכן';
        if (!String(d.system_prompt ?? '').trim()) return 'יש למלא הנחיות מערכת';
        return true;
      },
      render: ({ data, setData }: { data: Record<string, unknown>; setData: (p: Record<string, unknown>) => void }) => (
        <div className="stack">
          <FormField label="שם הסוכן" required>
            <TextInput value={String(data.name ?? '')} onChange={(e) => setData({ name: e.target.value })} placeholder="למשל: נציגת קבלת הזמנות" />
          </FormField>
          <FormField label="שיוך למרכזיה" hint="ריק = סוכן גלובלי (זמין לכל המרכזיות)">
            <FormSelect value={String(data.tenant_id ?? '')} onChange={(e) => setData({ tenant_id: e.target.value, flow_id: '', tool_ids: [] })}>
              <option value="">גלובלי</option>
              {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </FormSelect>
          </FormField>
          <FormField label="כיוון">
            <FormSelect value={String(data.direction ?? 'inbound')} onChange={(e) => setData({ direction: e.target.value })}>
              <option value="inbound">נכנס — עונה לשיחות</option>
              <option value="outbound">יוצא — מחייג ללקוחות</option>
              <option value="both">דו-כיווני</option>
            </FormSelect>
          </FormField>
          <FormField label="הנחיות מערכת" required hint="מי הסוכן, מה הוא יודע, איך הוא מדבר">
            <TextArea rows={6} value={String(data.system_prompt ?? '')} onChange={(e) => setData({ system_prompt: e.target.value })} placeholder="את נציגת שירות של פיצריית רומא. עברית בלבד, משפטים קצרים…" />
          </FormField>
        </div>
      ),
    },
    {
      id: 'voice', title: 'קול ומודל', optional: true,
      render: ({ data, setData }: { data: Record<string, unknown>; setData: (p: Record<string, unknown>) => void }) => (
        <div className="stack">
          <FormField label="מנוע הקול" hint="אפשר לשנות ולכוונן הכל אחר-כך בסטודיו">
            <FormSelect value={String(data.voice_provider ?? 'gemini')} onChange={(e) => setData({ voice_provider: e.target.value })}>
              <option value="gemini">Gemini — מובנה, מהיר (ברירת מחדל)</option>
              <option value="elevenlabs">ElevenLabs — איכות עברית גבוהה</option>
            </FormSelect>
          </FormField>
          <FormField label="מודל שיחה" hint={modelHint(liveModels, String(data.gemini_model ?? DEFAULT_MODEL))}>
            <ModelSelect
              models={liveModels}
              value={String(data.gemini_model ?? DEFAULT_MODEL)}
              onChange={(v) => setData({ gemini_model: v })}
            />
          </FormField>
          <FormField label="משפט פתיחה" hint="מה הסוכן אומר כשעונה (אופציונלי)">
            <TextInput value={String(data.greeting_text ?? '')} onChange={(e) => setData({ greeting_text: e.target.value })} placeholder="שלום! הגעתם לפיצריית רומא, איך אפשר לעזור?" />
          </FormField>
        </div>
      ),
    },
    {
      id: 'flow', title: 'זרימה', optional: true,
      render: ({ data, setData }: { data: Record<string, unknown>; setData: (p: Record<string, unknown>) => void }) => {
        const tid = data.tenant_id ? Number(data.tenant_id) : null;
        const visibleFlows = flows.filter((f) => f.tenant_id == null || f.tenant_id === tid);
        return (
          <div className="stack">
            <p style={{ margin: 0, fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>
              בלי זרימה הסוכן מנהל שיחה חופשית לפי ההנחיות. זרימה מובנית מובילה
              את השיחה בשלבים. אפשר גם ליצור זרימה חדשה אחר-כך מטאב הזרימה בסטודיו.
            </p>
            <FormField label="זרימת שיחה">
              <FormSelect value={String(data.flow_id ?? '')} onChange={(e) => setData({ flow_id: e.target.value })}>
                <option value="">ללא — שיחה חופשית</option>
                {visibleFlows.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </FormSelect>
            </FormField>
          </div>
        );
      },
    },
    {
      id: 'tools', title: 'כלי API', optional: true,
      render: ({ data, setData }: { data: Record<string, unknown>; setData: (p: Record<string, unknown>) => void }) => {
        const tid = data.tenant_id ? Number(data.tenant_id) : null;
        const visible = libraryTools.filter((t) => t.tenant_id == null || t.tenant_id === tid);
        const ids = (data.tool_ids as number[]) ?? [];
        return (
          <div className="stack">
            <p style={{ margin: 0, fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>
              כלי API מאפשר לסוכן לבדוק ולשלוח מידע בזמן שיחה (מלאי, הזמנות, CRM).
              יצירת כלים חדשים — מטאב הכלים בסטודיו.
            </p>
            {visible.length === 0
              ? <span style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>אין כלים בשיוך הזה עדיין.</span>
              : visible.map((t) => (
                <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 'var(--t-sm)' }}>
                  <input
                    type="checkbox"
                    checked={ids.includes(t.id)}
                    onChange={() => setData({ tool_ids: ids.includes(t.id) ? ids.filter((x) => x !== t.id) : [...ids, t.id] })}
                  />
                  <span style={{ fontWeight: 600 }}>{t.name}</span>
                  <span dir="ltr" style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>{t.method} {t.url}</span>
                </label>
              ))}
          </div>
        );
      },
    },
    {
      id: 'done', title: 'סיכום',
      render: ({ data }: { data: Record<string, unknown> }) => {
        const flowName = data.flow_id ? flows.find((f) => f.id === Number(data.flow_id))?.name : null;
        const toolCount = ((data.tool_ids as number[]) ?? []).length;
        return (
          <div className="stack">
            <Card title={String(data.name ?? '')}>
              <div className="stack" style={{ fontSize: 'var(--t-sm)' }}>
                <div>שיוך: {data.tenant_id ? tenants.find((t) => t.id === Number(data.tenant_id))?.name : 'גלובלי'}</div>
                <div>זרימה: {flowName ?? 'ללא — שיחה חופשית'}</div>
                <div>כלי API: {toolCount ? `${toolCount} כלים` : 'ללא'}</div>
              </div>
            </Card>
            <p style={{ margin: 0, fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>
              הסוכן ייווצר במצב טיוטה וניפתח בסטודיו — שם משלימים מספר טלפון,
              מפעילים, ועושים שיחת נסיון.
            </p>
          </div>
        );
      },
    },
  ];

  return (
    <Modal isOpen onClose={onClose} title="סוכן חדש — אשף מודרך" size="lg"
      footer={<button type="button" className="btn ghost" onClick={onFullForm}>מעבר לטופס המלא</button>}>
      <Wizard steps={steps} initialData={{ direction: 'inbound', voice_provider: 'gemini', gemini_model: DEFAULT_MODEL, tool_ids: [] }} onFinish={(d) => void finish(d)} onCancel={onClose} />
    </Modal>
  );
}

function VoiceAgentForm({
  mode, agent, tools, libraryTools, flows, tenants, onClose, onSaved,
}: {
  mode: 'create' | 'edit';
  agent?: VoiceAgentRow;
  tools: VoiceToolCatalogItem[];
  libraryTools: VoiceTool[];
  flows: VoiceFlowRow[];
  tenants: TenantLite[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: agent?.name ?? '',
    tenant_id: agent?.tenant_id != null ? String(agent.tenant_id) : '',
    status: (agent?.status ?? 'draft') as VoiceStatus,
    direction: (agent?.direction ?? 'inbound') as VoiceDirection,
    system_prompt: agent?.system_prompt ?? '',
    greeting_text: agent?.greeting_text ?? '',
    voice_name: agent?.voice_name ?? '',
    gemini_model: agent?.gemini_model ?? DEFAULT_MODEL,
    language: agent?.language ?? 'he-IL',
    max_call_seconds: agent?.max_call_seconds != null ? String(agent.max_call_seconds) : '',
    memory_enabled: agent?.memory_enabled ?? true,
    voice_provider: ((agent?.settings as { voice_provider?: string } | undefined)?.voice_provider ?? 'gemini') as 'gemini' | 'elevenlabs',
    // 'local' = השיחה מנוהלת אצלנו; 'elevenlabs' = הסוכן מתארח אצלם.
    agent_host: ((agent?.settings as { agent_host?: string } | undefined)?.agent_host ?? 'local') as 'local' | 'elevenlabs',
    elevenlabs_agent_id: (agent?.settings as { elevenlabs_agent_id?: string } | undefined)?.elevenlabs_agent_id ?? '',
    elevenlabs_voice_id: (agent?.settings as { elevenlabs_voice_id?: string } | undefined)?.elevenlabs_voice_id ?? '',
    elevenlabs_model_id: (agent?.settings as { elevenlabs_model_id?: string } | undefined)?.elevenlabs_model_id ?? 'eleven_v3',
    elevenlabs_stability: ((agent?.settings as { elevenlabs_stability?: string } | undefined)?.elevenlabs_stability ?? 'natural') as string,
    elevenlabs_humanize: (agent?.settings as { elevenlabs_humanize?: boolean } | undefined)?.elevenlabs_humanize ?? false,
    // סגנון דיבור: לשון פנייה + מילון הגייה (settings keys).
    address_style: ((agent?.settings as { address_style?: string } | undefined)?.address_style ?? 'neutral') as string,
    pronunciation_text: Object.entries(
      (agent?.settings as { pronunciation_map?: Record<string, string> } | undefined)?.pronunciation_map ?? {},
    ).map(([k, v]) => `${k} = ${v}`).join('\n'),
    // ── פרמטרים מתקדמים ──
    // מודל השיחה — שדות top-level על הסוכן.
    thinking_level: (agent as { thinking_level?: string } | undefined)?.thinking_level ?? '',
    temperature: (agent as { temperature?: number | null } | undefined)?.temperature != null ? String((agent as { temperature?: number }).temperature) : '',
    // קצב שיחה (VAD) — settings keys.
    vad_silence_ms: (agent?.settings as { vad_silence_ms?: number } | undefined)?.vad_silence_ms ?? 400,
    vad_end_sensitivity: (agent?.settings as { vad_end_sensitivity?: string } | undefined)?.vad_end_sensitivity ?? '',
    vad_start_sensitivity: (agent?.settings as { vad_start_sensitivity?: string } | undefined)?.vad_start_sensitivity ?? '',
    vad_prefix_ms: (agent?.settings as { vad_prefix_ms?: number } | undefined)?.vad_prefix_ms ?? 60,
    // קול ElevenLabs — settings keys.
    elevenlabs_similarity: (agent?.settings as { elevenlabs_similarity?: number } | undefined)?.elevenlabs_similarity ?? 0.75,
    elevenlabs_speaker_boost: (agent?.settings as { elevenlabs_speaker_boost?: boolean } | undefined)?.elevenlabs_speaker_boost ?? true,
    // ── Voice v3 ──
    knowledge_base_id: (agent as { knowledge_base_id?: number | null } | undefined)?.knowledge_base_id != null ? String((agent as { knowledge_base_id?: number }).knowledge_base_id) : '',
    webhook_enabled: ((agent?.settings as { webhook?: { enabled?: boolean } } | undefined)?.webhook?.enabled) ?? false,
    qa_scoring: ((agent?.settings as { qa_scoring?: boolean } | undefined)?.qa_scoring) ?? true,
    hours_mode: ((agent?.settings as { hours?: { mode?: string } } | undefined)?.hours?.mode ?? 'off') as string,
    noise_profile: ((agent?.settings as { noise_profile?: string } | undefined)?.noise_profile ?? 'normal') as string,
    payment_method: (((agent?.settings as { payment?: { method?: string } } | undefined)?.payment?.method) ?? 'none') as string,
    payment_provider: (((agent?.settings as { payment?: { provider?: string } } | undefined)?.payment?.provider) ?? 'green_invoice') as string,
    payment_send_trigger_token: ((agent?.settings as { payment?: { send_trigger_token?: string } } | undefined)?.payment?.send_trigger_token) ?? '',
    payment_max_amount: ((agent?.settings as { payment?: { max_amount?: number } } | undefined)?.payment?.max_amount) ?? '',
    payment_description_template: ((agent?.settings as { payment?: { description_template?: string } } | undefined)?.payment?.description_template) ?? '',
    after_hours_message: ((agent?.settings as { after_hours?: { message?: string } } | undefined)?.after_hours?.message) ?? '',
  });
  const [webhookInclude, setWebhookInclude] = useState<string[]>(
    ((agent?.settings as { webhook?: { include?: string[] } } | undefined)?.webhook?.include) ?? ['variables', 'summary'],
  );
  const [transferTargets, setTransferTargets] = useState<TransferTargetDraft[]>(
    (((agent?.settings as { transfer_targets?: TransferTargetDraft[] } | undefined)?.transfer_targets) ?? []).map((t) => ({ ...t })),
  );
  // לוח שעות פר-סוכן (hours.mode='custom'): שבעה ימים תמיד, כדי שהטופס יהיה
  // עריך גם כשהשרת שמר רק חלק מהם. יום חסר = סגור.
  const [hoursCustom, setHoursCustom] = useState<HoursDayDraft[]>(() =>
    mergeHoursDays((agent?.settings as { hours?: { custom?: HoursDayDraft[] } } | undefined)?.hours?.custom),
  );
  const [kbList, setKbList] = useState<{ id: number; name: string; business_name?: string | null }[]>([]);
  useEffect(() => {
    void superApi.voiceKnowledgeBases().then(setKbList).catch(() => {});
  }, []);
  const [liveModels, setLiveModels] = useState<VoiceLiveModel[]>([]);
  useEffect(() => {
    void superApi.voiceModels().then((r) => setLiveModels(r.models)).catch(() => {});
  }, []);
  // קטלוג OpenAI — נטען רק כשמודל השיחה באמת רץ על המנוע הזה.
  const isOpenAiEngine = (liveModels.find((m) => m.id === form.gemini_model)?.engine ?? 'gemini') === 'openai';
  // סוכן מתארח: מודל השיחה, הכלים והזרימה חיים אצל ElevenLabs, ולכן
  // חלק מהשדות במסך אינם רלוונטיים לו.
  const isHosted = form.agent_host === 'elevenlabs';
  const [oaCatalog, setOaCatalog] = useState<OpenAiCatalog | null>(null);
  useEffect(() => {
    if (isOpenAiEngine && !oaCatalog) void superApi.openaiCatalog().then(setOaCatalog).catch(() => {});
  }, [isOpenAiEngine, oaCatalog]);
  const [elevenTags, setElevenTags] = useState<string[]>((agent?.settings as { elevenlabs_tags?: string[] } | undefined)?.elevenlabs_tags ?? []);
  const [v3Open, setV3Open] = useState(false);
  const [advOpen, setAdvOpen] = useState(false);
  const [elCatalog, setElCatalog] = useState<ElevenLabsCatalog | null>(null);
  useEffect(() => {
    if (form.voice_provider === 'elevenlabs' && !elCatalog) {
      void superApi.elevenLabsCatalog?.().then(setElCatalog).catch(() => {});
    }
  }, [form.voice_provider, elCatalog]);
  const [enabledTools, setEnabledTools] = useState<string[]>(agent?.enabled_tools ?? []);
  const [customTools, setCustomTools] = useState<VoiceCustomTool[]>(agent?.custom_tools ?? []);
  const [toolIds, setToolIds] = useState<number[]>(
    agent?.tool_ids ?? (agent?.library_tools ?? []).map((t) => t.id),
  );
  const [flowId, setFlowId] = useState<string>(agent?.flow_id != null ? String(agent.flow_id) : '');
  const [saving, setSaving] = useState(false);

  // כלי הספרייה מסוננים לפי שיוך: כלי גלובלי או כלי של אותה מרכזייה כמו הסוכן.
  const selectedTenant = form.tenant_id ? Number(form.tenant_id) : null;
  const visibleLibraryTools = libraryTools.filter(
    (t) => t.tenant_id == null || t.tenant_id === selectedTenant,
  );
  // זרימות זמינות: גלובליות או של אותה מרכזייה כמו הסוכן.
  const visibleFlows = flows.filter((f) => f.tenant_id == null || f.tenant_id === selectedTenant);

  function toggleTool(name: string) {
    setEnabledTools((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));
  }
  function toggleLibraryTool(id: number) {
    setToolIds((prev) => (prev.includes(id) ? prev.filter((n) => n !== id) : [...prev, id]));
  }

  function buildPayload(): Partial<VoiceAgentRow> {
    const cleanCustom = customTools
      .filter((t) => t.name?.trim() && t.url?.trim())
      .map((t) => ({
        name: t.name.trim(),
        url: t.url.trim(),
        description: t.description?.trim() || null,
        headers: t.headers && Object.keys(t.headers).length ? t.headers : null,
        body_template: t.body_template && Object.keys(t.body_template).length ? t.body_template : null,
        schema: t.schema && Object.keys(t.schema).length ? t.schema : null,
      }));
    return {
      name: form.name.trim(),
      tenant_id: form.tenant_id ? Number(form.tenant_id) : null,
      status: form.status,
      direction: form.direction,
      system_prompt: form.system_prompt,
      greeting_text: form.greeting_text || null,
      voice_name: form.voice_name || null,
      gemini_model: form.gemini_model || null,
      language: form.language || null,
      max_call_seconds: form.max_call_seconds ? Number(form.max_call_seconds) : null,
      memory_enabled: form.memory_enabled,
      enabled_tools: enabledTools,
      custom_tools: cleanCustom,
      tool_ids: toolIds,
      flow_id: flowId ? Number(flowId) : null,
      voice_provider: form.voice_provider,
      agent_host: form.agent_host,
      // נשלח רק כשהסוכן מתארח — אחרת שיוך ישן היה נשאר תלוי באוויר.
      elevenlabs_agent_id: form.agent_host === 'elevenlabs' ? (form.elevenlabs_agent_id || null) : undefined,
      elevenlabs_voice_id: form.voice_provider === 'elevenlabs' ? (form.elevenlabs_voice_id || null) : undefined,
      elevenlabs_model_id: form.voice_provider === 'elevenlabs' ? (form.elevenlabs_model_id || null) : undefined,
      elevenlabs_stability: form.voice_provider === 'elevenlabs' ? form.elevenlabs_stability : undefined,
      elevenlabs_humanize: form.voice_provider === 'elevenlabs' ? form.elevenlabs_humanize : undefined,
      elevenlabs_tags: form.voice_provider === 'elevenlabs' ? elevenTags : undefined,
      // פרמטרים מתקדמים — מודל השיחה (top-level; ריק = ברירת מחדל בשרת).
      thinking_level: form.thinking_level || null,
      temperature: form.temperature !== '' ? Number(form.temperature) : null,
      // קצב שיחה (VAD) — settings keys flat.
      vad_silence_ms: form.vad_silence_ms,
      vad_end_sensitivity: form.vad_end_sensitivity || null,
      vad_start_sensitivity: form.vad_start_sensitivity || null,
      vad_prefix_ms: form.vad_prefix_ms,
      // קול ElevenLabs — נשלח רק כשזה המנוע.
      elevenlabs_similarity: form.voice_provider === 'elevenlabs' ? form.elevenlabs_similarity : undefined,
      elevenlabs_speaker_boost: form.voice_provider === 'elevenlabs' ? form.elevenlabs_speaker_boost : undefined,
      // סגנון דיבור.
      address_style: form.address_style,
      pronunciation_map: parsePronunciationText(form.pronunciation_text),
      // ── Voice v3 ──
      knowledge_base_id: form.knowledge_base_id ? Number(form.knowledge_base_id) : null,
      webhook: { enabled: form.webhook_enabled, include: webhookInclude },
      qa_scoring: form.qa_scoring,
      // custom → שולחים את הלוח המלא; אחרת רק המצב, כדי לא לשמור לוח מת.
      hours: form.hours_mode === 'custom'
        ? { mode: 'custom', custom: hoursCustom.map((d) => ({ day: d.day, enabled: d.enabled, open: d.open, close: d.close })) }
        : { mode: form.hours_mode },
      noise_profile: form.noise_profile,
      payment: form.payment_method === 'none' ? { method: 'none' } : {
        method: form.payment_method,
        provider: form.payment_provider,
        send_trigger_token: form.payment_send_trigger_token || null,
        max_amount: form.payment_max_amount !== '' ? Number(form.payment_max_amount) : null,
        description_template: form.payment_description_template || null,
      },
      after_hours: { message: form.after_hours_message.trim() || null },
      transfer_targets: transferTargets
        .filter((t) => t.key?.trim() && t.target?.trim())
        .map((t) => ({
          key: t.key.trim(), label: t.label?.trim() || t.key.trim(),
          type: t.type, target: t.target.replace(/\D+/g, ''),
          hours_gated: !!t.hours_gated,
          // `when` (תנאי לפי משתנה שיחה) נערך היום רק דרך ה-MCP/API — משמרים
          // אותו כמו שהוא, אחרת כל שמירה מהטופס הייתה מוחקת אותו.
          ...(t.when ? { when: t.when } : {}),
        })),
    } as Partial<VoiceAgentRow>;
  }

  async function save() {
    if (!form.name.trim()) { toast('יש למלא שם', 'danger'); return; }
    if (!form.system_prompt.trim()) { toast('יש למלא הנחיות מערכת', 'danger'); return; }
    setSaving(true);
    try {
      if (mode === 'create') {
        await superApi.createVoiceAgent(buildPayload());
        toast('הסוכן נוצר', 'ok');
      } else if (agent) {
        const patch = changedOnly(buildPayload(), agent);
        if (Object.keys(patch).length === 0) {
          toast('לא שונה כלום', 'ok');
          onSaved();

          return;
        }
        await superApi.updateVoiceAgent(agent.id, patch);
        toast('עודכן', 'ok');
      }
      onSaved();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה בשמירה';
      toast(msg, 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="lg"
      title={mode === 'create' ? 'סוכן קול חדש' : `עריכת ${agent?.name}`}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={saving}>ביטול</button>
          <button type="button" className="btn primary" onClick={save} disabled={saving}>
            {saving ? 'שומר…' : 'שמור'}
          </button>
        </>
      }
    >
      <div className="stack">
        {/* 52 שדות בטופס — בלי ניווט המשתמש גולל מאות שורות כדי למצוא אחד.
            סוכן מתארח לא מציג כלים/פונקציות, ולכן הם יורדים גם מהניווט. */}
        <FormSectionNav
          sections={[
            { id: 'sec-voice', label: 'קול ומודל' },
            { id: 'sec-hours', label: 'שעות' },
            { id: 'sec-transfer', label: 'העברה' },
            ...(isHosted ? [] : [
              { id: 'sec-builtin', label: 'פונקציות' },
              { id: 'sec-library', label: 'ספרייה' },
              { id: 'sec-custom', label: 'API' },
            ]),
            { id: 'sec-payment', label: 'תשלום' },
            { id: 'sec-quality', label: 'איכות' },
          ]}
        />

        <FormField label="שם" required>
          <TextInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </FormField>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gap)' }}>
          <FormField label="שיוך" hint="ריק = סוכן גלובלי המשויך למרכזיות דרך הטאב שיוך">
            <FormSelect value={form.tenant_id} onChange={(e) => setForm({ ...form, tenant_id: e.target.value })}>
              <option value="">גלובלי (משותף)</option>
              {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </FormSelect>
          </FormField>
          <FormField label="כיוון">
            <FormSelect value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value as VoiceDirection })}>
              <option value="inbound">נכנס</option>
              <option value="outbound">יוצא</option>
              <option value="both">דו-כיווני</option>
            </FormSelect>
          </FormField>
        </div>

        <FormField label="הנחיות מערכת" required hint="האישיות וההתנהגות של הסוכן בשיחה">
          <TextArea rows={5} value={form.system_prompt} onChange={(e) => setForm({ ...form, system_prompt: e.target.value })} />
        </FormField>
        <FormField label="הודעת פתיחה" hint="המשפט הראשון שהסוכן אומר כשהשיחה מתחילה (אופציונלי)">
          <TextArea rows={2} value={form.greeting_text} onChange={(e) => setForm({ ...form, greeting_text: e.target.value })} />
        </FormField>

        <FormField label="זרימה" hint={visibleFlows.length === 0
          ? '⚠️ אין זרימות זמינות לשיוך הזה — צור זרימה בלשונית "זרימות", או שנה שם את השיוך של זרימה קיימת (ללקוח הזה או "גלובלי").'
          : 'ריק = ללא זרימה (שיחה חופשית לפי ההנחיות). זרימה מובנה מובילה את השיחה בשלבים.'}>
          <FormSelect value={flowId} onChange={(e) => setFlowId(e.target.value)}>
            <option value="">ללא זרימה</option>
            {visibleFlows.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </FormSelect>
        </FormField>

        <FormField label="מאגר ידע (RAG)" hint="חיבור מאגר מפעיל אוטומטית את הכלי search_knowledge — הסוכן עונה מתוך המאגר במקום להמציא.">
          <FormSelect value={form.knowledge_base_id} onChange={(e) => setForm({ ...form, knowledge_base_id: e.target.value })}>
            <option value="">ללא מאגר ידע</option>
            {kbList.map((kb) => <option key={kb.id} value={kb.id}>{kb.name}{kb.business_name ? ` (${kb.business_name})` : ''}</option>)}
          </FormSelect>
        </FormField>

        <FormSection id="sec-quality" title="סוף שיחה ואיכות" subtitle="ציון QA, webhook, סיכום">
        <Toggle checked={form.qa_scoring} onChange={(v) => setForm({ ...form, qa_scoring: v })} label="ציון QA אוטומטי אחרי כל שיחה (Gemini מדרג נאמנות/נימוס/דיוק)" />
        <Toggle checked={form.webhook_enabled} onChange={(v) => setForm({ ...form, webhook_enabled: v })} label="Webhook בסוף שיחה — יורה voice_call.ended ל-webhooks של המרכזיה" />
        {form.webhook_enabled && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, paddingInlineStart: 8 }}>
            {([['variables', 'משתני שיחה'], ['summary', 'סיכום'], ['transcript', 'תמלול מלא'], ['tools_used', 'כלים שהופעלו']] as const).map(([k, label]) => (
              <label key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--t-sm)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={webhookInclude.includes(k)}
                  onChange={() => setWebhookInclude((prev) => prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k])}
                />
                {label}
              </label>
            ))}
          </div>
        )}

        <FormField label="סביבת רעש" hint={form.noise_profile === 'noisy'
          ? 'סביבה רועשת: הסוכן פחות קופצני, מחכה יותר לפני תשובה, ומסנן רעש רקע חזק יותר.'
          : form.noise_profile === 'off'
            ? 'סינון הרעש כבוי — מדדי איכות האודיו עדיין נמדדים.'
            : 'סינון רעש עדין (ברירת מחדל) — מתאים לרוב השיחות.'}>
          <FormSelect value={form.noise_profile} onChange={(e) => setForm({ ...form, noise_profile: e.target.value })}>
            <option value="normal">רגיל</option>
            <option value="noisy">סביבה רועשת</option>
            <option value="off">כבוי</option>
          </FormSelect>
        </FormField>
        </FormSection>

        <FormSection id="sec-hours" title="שעות פעילות" subtitle="מתי הסוכן עונה">
        <FormField label="מענה מחוץ לשעות" hint={form.hours_mode === 'off' ? 'הסוכן עונה תמיד.' : 'מחוץ לשעות (כולל חגים) — הודעה קולית וניתוק, בלי עלות AI.'}>
          <FormSelect value={form.hours_mode} onChange={(e) => setForm({ ...form, hours_mode: e.target.value })}>
            <option value="off">תמיד זמין</option>
            <option value="tenant">לפי שעות המרכזיה</option>
            <option value="custom">לוח שעות משלו</option>
          </FormSelect>
        </FormField>
        {form.hours_mode === 'custom' && (
          <div style={{ display: 'grid', gap: 4 }}>
            <p style={{ margin: 0, fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
              יום שאינו מסומן — הסוכן לא עונה בו. חגי המרכזיה סוגרים גם לוח משלו.
            </p>
            {hoursCustom.map((d, i) => (
              <div key={d.day} style={{ display: 'grid', gridTemplateColumns: '90px 1fr 1fr', gap: 6, alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--t-sm)' }}>
                  <input
                    type="checkbox"
                    checked={d.enabled}
                    aria-label={`פעיל ביום ${DAY_LABEL[d.day]}`}
                    onChange={(e) => setHoursCustom((p) => p.map((x, j) => j === i ? { ...x, enabled: e.target.checked } : x))}
                  />
                  {DAY_LABEL[d.day]}
                </label>
                <TextInput
                  type="time" value={d.open} disabled={!d.enabled} dir="ltr" aria-label={`פתיחה ${DAY_LABEL[d.day]}`}
                  onChange={(e) => setHoursCustom((p) => p.map((x, j) => j === i ? { ...x, open: e.target.value } : x))}
                />
                <TextInput
                  type="time" value={d.close} disabled={!d.enabled} dir="ltr" aria-label={`סגירה ${DAY_LABEL[d.day]}`}
                  onChange={(e) => setHoursCustom((p) => p.map((x, j) => j === i ? { ...x, close: e.target.value } : x))}
                />
              </div>
            ))}
          </div>
        )}
        {form.hours_mode !== 'off' && (
          <FormField label="הודעת מחוץ-לשעות" hint="ריק = הודעת ברירת מחדל. מנוגנת בקול הסוכן.">
            <TextArea rows={2} value={form.after_hours_message} onChange={(e) => setForm({ ...form, after_hours_message: e.target.value })} />
          </FormField>
        )}
        </FormSection>

        <FormSection id="sec-transfer" title="העברה לנציג" subtitle="יעדי העברה">
        <p style={{ margin: 0, fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
          יעדים שהכלי transfer_to_human רשאי להעביר אליהם (שלוחה פנימית או מספר חיצוני). הסוכן בוחר יעד מהרשימה בלבד — לעולם לא מספר חופשי. ההעברה מוקלטת.
        </p>
        {transferTargets.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto auto', gap: 6, fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
            <span>מפתח (לועזי)</span><span>תווית</span><span>סוג</span><span>יעד</span><span>בשעות בלבד</span><span />
          </div>
        )}
        {transferTargets.map((t, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto auto', gap: 6, alignItems: 'center' }}>
            <TextInput value={t.key} dir="ltr" placeholder="sales" aria-label="מפתח" onChange={(e) => setTransferTargets((p) => p.map((x, j) => j === i ? { ...x, key: e.target.value } : x))} />
            <TextInput value={t.label ?? ''} placeholder="מכירות" aria-label="תווית" onChange={(e) => setTransferTargets((p) => p.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} />
            <FormSelect value={t.type} aria-label="סוג" onChange={(e) => setTransferTargets((p) => p.map((x, j) => j === i ? { ...x, type: e.target.value as 'extension' | 'external' } : x))}>
              <option value="extension">שלוחה פנימית</option>
              <option value="external">מספר חיצוני</option>
            </FormSelect>
            <TextInput value={t.target} dir="ltr" placeholder={t.type === 'extension' ? '4001' : '0501234567'} aria-label="יעד" onChange={(e) => setTransferTargets((p) => p.map((x, j) => j === i ? { ...x, target: e.target.value } : x))} />
            <input
              type="checkbox"
              checked={!!t.hours_gated}
              aria-label={`העברה רק בשעות הפעילות — ${t.label?.trim() || t.key || 'יעד'}`}
              title="ההעברה תתאפשר רק בתוך שעות הפעילות של הסוכן"
              onChange={(e) => setTransferTargets((p) => p.map((x, j) => j === i ? { ...x, hours_gated: e.target.checked } : x))}
            />
            <button type="button" className="btn ghost" aria-label="הסר יעד" onClick={() => setTransferTargets((p) => p.filter((_, j) => j !== i))}>✕</button>
          </div>
        ))}
        <div>
          <button type="button" className="btn ghost" onClick={() => setTransferTargets((p) => [...p, { key: '', label: '', type: 'extension', target: '' }])}>+ יעד העברה</button>
        </div>
        </FormSection>

        <FormSection id="sec-payment" title="תשלום בשיחה" subtitle="גבייה תוך כדי שיחה">
        <FormField label="שיטת גבייה" hint={form.payment_method === 'link'
          ? 'קישור תשלום נשלח ללקוח בוואטסאפ אחרי אישור ההזמנה.'
          : form.payment_method === 'dtmf'
            ? 'הלקוח מקיש את פרטי הכרטיס במקשי הטלפון (מאובטח — לא נשמע ולא מוקלט).'
            : 'ללא גבייה בשיחה.'}>
          <FormSelect value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
            <option value="none">ללא</option>
            <option value="link">קישור תשלום בוואטסאפ</option>
            <option value="dtmf">הקלדת כרטיס במקשים</option>
          </FormSelect>
        </FormField>
        {form.payment_method !== 'none' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gap)' }}>
              <FormField label="ספק סליקה">
                <FormSelect value={form.payment_provider} onChange={(e) => setForm({ ...form, payment_provider: e.target.value })}>
                  {form.payment_method === 'link' ? (
                    <>
                      <option value="green_invoice">חשבונית ירוקה</option>
                      <option value="generic">ספק אחר (כלי מותאם)</option>
                    </>
                  ) : (
                    <option value="cardcom">CardCom</option>
                  )}
                </FormSelect>
              </FormField>
              <FormField label="סכום מרבי (₪)" hint="חיוב מעל הסכום ייחסם">
                <TextInput value={String(form.payment_max_amount)} dir="ltr" inputMode="numeric" onChange={(e) => setForm({ ...form, payment_max_amount: e.target.value })} />
              </FormField>
            </div>
            {form.payment_method === 'link' && (
              <FormField label="טוקן טריגר לשליחה בוואטסאפ" hint="הטוקן של טריגר ההודעות ששולח את הקישור ללקוח">
                <TextInput value={form.payment_send_trigger_token} dir="ltr" onChange={(e) => setForm({ ...form, payment_send_trigger_token: e.target.value })} />
              </FormField>
            )}
            <FormField label="תיאור עסקה (ברירת מחדל)">
              <TextInput value={form.payment_description_template} onChange={(e) => setForm({ ...form, payment_description_template: e.target.value })} placeholder="הזמנה טלפונית" />
            </FormField>
            <p style={{ margin: 0, fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
              פרטי הסליקה (מפתחות API / מסוף CardCom) מוגדרים בנפרד בטאב "מספרים ותשלום" של הסוכן.
            </p>
          </>
        )}
        </FormSection>

        <FormSection id="sec-voice" title="קול ומודל" subtitle="סוג הסוכן, מודל שיחה, קול">
        {/* סוג הסוכן: שני הראשונים מנוהלים אצלנו ונבדלים רק במנוע הקול;
            השלישי מתארח כולו ב-ElevenLabs, ואז מודל השיחה והכלים חיים שם. */}
        <FormField
          label="סוג הסוכן"
          hint={isHosted
            ? 'הסוכן מתארח כולו ב-ElevenLabs: ASR, מודל שיחה וקול. ההגדרות נערכות כאן ונדחפות אליהם.'
            : form.voice_provider === 'elevenlabs'
              ? `הקול מסונתז מחדש ב-ElevenLabs: איכות עברית גבוהה, אבל האודיו של ${engineLabelFor(liveModels, form.gemini_model)} נזרק ונוסף תור סינתוז (השהיה).`
              : `הקול המובנה של ${engineLabelFor(liveModels, form.gemini_model)} — ישירות לקו, ללא השהיה נוספת.`}
        >
          <FormSelect
            value={isHosted ? 'hosted' : form.voice_provider}
            onChange={(e) => {
              const v = e.target.value;
              setForm({
                ...form,
                agent_host: v === 'hosted' ? 'elevenlabs' : 'local',
                // ערך 'gemini' הוא היסטורי — משמעותו "הקול המובנה של המנוע",
                // ונכון גם כשמודל השיחה הוא OpenAI.
                voice_provider: v === 'elevenlabs' ? 'elevenlabs' : 'gemini',
              });
            }}
          >
            <option value="gemini">{`קול מובנה — ${engineLabelFor(liveModels, form.gemini_model)} (מהיר)`}</option>
            <option value="elevenlabs">ElevenLabs כמנוע קול (איכות עברית, +השהיה)</option>
            <option value="hosted">מתארח ב-ElevenLabs Agents</option>
          </FormSelect>
        </FormField>

        {isHosted && (
          <HostedElevenLabsPanel
            agentId={agent?.id ?? null}
            value={form.elevenlabs_agent_id}
            onChange={(v) => setForm({ ...form, elevenlabs_agent_id: v })}
            lastDeployedAt={agent?.last_deployed_at ?? null}
            enabledTools={enabledTools}
          />
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--gap)' }}>
          <FormField
            label="מודל השיחה"
            hint={isHosted
              ? 'במסלול המתארח המודל אינו רץ אצלנו — הבחירה כאן ממופה ל-LLM המקביל אצל ElevenLabs.'
              : modelHint(liveModels, form.gemini_model)}
          >
            <ModelSelect models={liveModels} value={form.gemini_model} onChange={(v) => setForm({ ...form, gemini_model: v })} />
          </FormField>
          {/* סוכן מתארח מסנתז תמיד ב-ElevenLabs, גם כש-voice_provider הוא
              'gemini' — ולכן בוררי הקול שלהם חייבים להופיע גם כאן. */}
          {(form.voice_provider === 'elevenlabs' || isHosted) ? (
            <>
              {/* ב-ConvAI רק eleven_v3_conversational מקבל language=he —
                  בורר פתוח כאן היה מאפשר בחירה שנכשלת בדחיפה. */}
              {isHosted ? (
                <FormField label="מודל ElevenLabs" hint="המודל היחיד ב-ConvAI שתומך עברית — נקבע אוטומטית.">
                  <TextInput value="eleven_v3_conversational" dir="ltr" disabled readOnly />
                </FormField>
              ) : (
                <FormField label="מודל ElevenLabs" hint={elCatalog?.models.find((m) => m.id === form.elevenlabs_model_id)?.hebrew ? 'תומך עברית ✓' : undefined}>
                  <FormSelect value={form.elevenlabs_model_id} onChange={(e) => setForm({ ...form, elevenlabs_model_id: e.target.value })}>
                    {(elCatalog?.models ?? [{ id: 'eleven_v3', name: 'Eleven v3 (עברית)', hebrew: true }]).map((m) => (
                      <option key={m.id} value={m.id}>{m.name}{m.hebrew ? ' — עברית' : ''}</option>
                    ))}
                  </FormSelect>
                </FormField>
              )}
              <FormField label="קול ElevenLabs">
                {elCatalog?.voices?.length ? (
                  <FormSelect value={form.elevenlabs_voice_id} onChange={(e) => setForm({ ...form, elevenlabs_voice_id: e.target.value })}>
                    <option value="">— בחר קול —</option>
                    {elCatalog.voices.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </FormSelect>
                ) : (
                  <TextInput value={form.elevenlabs_voice_id} onChange={(e) => setForm({ ...form, elevenlabs_voice_id: e.target.value })} dir="ltr" placeholder="Voice ID" />
                )}
              </FormField>
            </>
          ) : (
            <>
              {isOpenAiEngine ? (
                <FormField label="קול OpenAI" hint={oaCatalog?.configured === false ? 'חסר מפתח OpenAI — השיחה תיפול חזרה ל-Gemini.' : undefined}>
                  <OpenAiVoicePicker
                    voices={oaCatalog?.voices ?? []}
                    value={form.voice_name || 'marin'}
                    onChange={(v) => setForm({ ...form, voice_name: v })}
                  />
                </FormField>
              ) : (
                <FormField label="קול Gemini" hint="לדוגמה Kore">
                  <TextInput value={form.voice_name} onChange={(e) => setForm({ ...form, voice_name: e.target.value })} dir="ltr" />
                </FormField>
              )}
              {/* ל-OpenAI Realtime אין בורר שפה — היא נגזרת מההנחיות. */}
              {!isOpenAiEngine && (
                <FormField label="שפה">
                  <TextInput value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} dir="ltr" />
                </FormField>
              )}
            </>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 'var(--gap)' }}>
          <FormField label="לשון פנייה" hint="ניטרלי = פותח ברבים ועובר לזכר/נקבה לפי זיהוי מהקול/שם/תיקון">
            <FormSelect value={form.address_style} onChange={(e) => setForm({ ...form, address_style: e.target.value })}>
              <option value="neutral">ניטרלי-אדפטיבי (מומלץ)</option>
              <option value="male">זכר קבוע</option>
              <option value="female">נקבה קבועה</option>
            </FormSelect>
          </FormField>
          <FormField label="מילון הגייה" hint="שורה לכל מילה: מילה = איות מנוקד/פונטי. מוחל על הדיבור בלבד, לא על התמלול">
            <TextArea
              rows={2}
              dir="rtl"
              value={form.pronunciation_text}
              onChange={(e) => setForm({ ...form, pronunciation_text: e.target.value })}
              placeholder={'קליAI = קָאלִי אֵיי אַיי\nSarah = שרה'}
            />
          </FormField>
        </div>

        {form.voice_provider === 'elevenlabs' && (
          form.elevenlabs_model_id.startsWith('eleven_v3') ? (
            <ElevenV3Panel
              open={v3Open}
              onToggleOpen={() => setV3Open((o) => !o)}
              stability={form.elevenlabs_stability}
              onStability={(s) => setForm({ ...form, elevenlabs_stability: s })}
              humanize={form.elevenlabs_humanize}
              onHumanize={(v) => setForm({ ...form, elevenlabs_humanize: v })}
              tags={elevenTags}
              onTags={setElevenTags}
              catalog={elCatalog}
            />
          ) : (
            <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="info" size={14} /> תגיות אודיו ודיבור אנושי זמינים רק ב-Eleven v3.
            </div>
          )
        )}

        <AdvancedParamsPanel
          open={advOpen}
          onToggleOpen={() => setAdvOpen((o) => !o)}
          isOpenAiEngine={isOpenAiEngine}
          values={{
            thinking_level: form.thinking_level,
            temperature: form.temperature,
            vad_silence_ms: form.vad_silence_ms,
            vad_end_sensitivity: form.vad_end_sensitivity,
            vad_start_sensitivity: form.vad_start_sensitivity,
            vad_prefix_ms: form.vad_prefix_ms,
            elevenlabs_similarity: form.elevenlabs_similarity,
            elevenlabs_speaker_boost: form.elevenlabs_speaker_boost,
          }}
          onPatch={(p) => setForm((f) => ({ ...f, ...p }))}
          isElevenlabs={form.voice_provider === 'elevenlabs'}
        />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gap)', alignItems: 'end' }}>
          <FormField label="סטטוס">
            <FormSelect value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as VoiceStatus })}>
              <option value="draft">טיוטה</option>
              <option value="active">פעיל</option>
              <option value="inactive">כבוי</option>
            </FormSelect>
          </FormField>
          <FormField label="משך שיחה מקסימלי (שניות)" hint="ריק = ברירת המחדל במערכת">
            <TextInput type="number" value={form.max_call_seconds} onChange={(e) => setForm({ ...form, max_call_seconds: e.target.value })} dir="ltr" />
          </FormField>
        </div>
        <Toggle
          checked={form.memory_enabled}
          onChange={(v) => setForm({ ...form, memory_enabled: v })}
          label="זיכרון לקוח — להזריק תמצית של שיחות קודמות עם אותו מתקשר"
        />
        </FormSection>

        <FormSection id="sec-builtin" title="פונקציות מובנות" subtitle="כלים מובנים שהסוכן מפעיל בשיחה">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {tools.length === 0
            ? <span style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>קטלוג הפונקציות לא נטען.</span>
            : tools.map((t) => (
              <label
                key={t.name}
                title={t.description}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                  padding: '6px 10px', borderRadius: 'var(--r-2)',
                  border: `1px solid ${enabledTools.includes(t.name) ? 'var(--accent)' : 'var(--line)'}`,
                  background: enabledTools.includes(t.name) ? 'var(--accent-soft, var(--surface-2))' : 'var(--surface)',
                  fontSize: 'var(--t-sm)',
                }}
              >
                <input type="checkbox" checked={enabledTools.includes(t.name)} onChange={() => toggleTool(t.name)} />
                <span dir="ltr">{t.name}</span>
              </label>
            ))}
        </div>
        </FormSection>

        <FormSection id="sec-library" title="כלים מהספרייה" subtitle="כלי API משותפים">
          <p style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)', margin: '4px 0 0' }}>
            כלי API מנוהלים מרכזית (מסך "כלי API"). מוצגים כלים גלובליים וכלים של המרכזייה המשויכת.
          </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {visibleLibraryTools.length === 0
            ? <span style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>אין כלים זמינים בספרייה.</span>
            : visibleLibraryTools.map((t) => (
              <label
                key={t.id}
                title={t.description ?? undefined}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                  padding: '6px 10px', borderRadius: 'var(--r-2)',
                  border: `1px solid ${toolIds.includes(t.id) ? 'var(--accent)' : 'var(--line)'}`,
                  background: toolIds.includes(t.id) ? 'var(--accent-soft, var(--surface-2))' : 'var(--surface)',
                  fontSize: 'var(--t-sm)',
                }}
              >
                <input type="checkbox" checked={toolIds.includes(t.id)} onChange={() => toggleLibraryTool(t.id)} />
                <span dir="ltr">{t.name}</span>
              </label>
            ))}
        </div>
        </FormSection>

        <FormSection id="sec-custom" title="פונקציות מותאמות (API חיצוני)" subtitle="webhooks משלך">
          <p style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)', margin: '4px 0 0' }}>
            הסוכן יכול לקרוא ל-API חיצוני תוך כדי שיחה (בדיקת חבילה, מלאי וכו'). רק https
            לכתובת ציבורית. הגדר כותרות (למשל x-api-key), תבנית גוף עם placeholders, וסכימת
            פרמטרים כדי שהסוכן ידע אילו ערכים למלא.
          </p>
        {customTools.map((tool, i) => {
          const patch = (p: Partial<VoiceCustomTool>) =>
            setCustomTools((prev) => prev.map((t, j) => (j === i ? { ...t, ...p } : t)));
          return (
            <div key={i} style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', padding: 10, display: 'grid', gap: 6 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 8, alignItems: 'center' }}>
                <TextInput placeholder="שם הפונקציה (check_package)" value={tool.name}
                  onChange={(e) => patch({ name: e.target.value })} dir="ltr" />
                <TextInput placeholder="https://…/api" value={tool.url}
                  onChange={(e) => patch({ url: e.target.value })} dir="ltr" />
                <button type="button" className="btn danger" onClick={() => setCustomTools((prev) => prev.filter((_, j) => j !== i))}>הסר</button>
              </div>
              <TextInput placeholder="תיאור לסוכן — מתי להשתמש (למשל: בדיקת סטטוס חבילה לפי מספר מעקב)"
                value={tool.description ?? ''} onChange={(e) => patch({ description: e.target.value })} />
              <JsonField label="כותרות (headers) — למשל x-api-key" value={tool.headers}
                onChange={(v) => patch({ headers: v as Record<string, string> | null })}
                placeholder='{"x-api-key": "..."}' />
              <JsonField label="תבנית גוף הבקשה — placeholders {arg} מהסכימה" value={tool.body_template}
                onChange={(v) => patch({ body_template: v })}
                placeholder='{"action": "get_order_status", "tracking_number": "{tracking_number}"}' />
              <JsonField label="סכימת פרמטרים (JSON-Schema) — מה הסוכן ממלא" value={tool.schema}
                onChange={(v) => patch({ schema: v })}
                placeholder='{"type":"object","properties":{"tracking_number":{"type":"string","description":"מספר מעקב"}},"required":["tracking_number"]}' />
            </div>
          );
        })}
        <div>
          <button type="button" className="btn" onClick={() => setCustomTools((prev) => [...prev, { name: '', url: '' }])}>
            + פונקציית API
          </button>
        </div>
        </FormSection>
      </div>
    </Modal>
  );
}

// ── פאנל יכולות Eleven v3 (מצב יציבות, דיבור אנושי, תגיות אודיו) ──────────────
const V3_STABILITY: { value: string; label: string; hint: string }[] = [
  { value: 'creative', label: 'יצירתי', hint: 'הכי אקספרסיבי, תגיות עובדות הכי טוב' },
  { value: 'natural', label: 'טבעי', hint: 'מאוזן' },
  { value: 'robust', label: 'יציב', hint: 'עקבי, פחות מגיב לתגיות' },
];

function ElevenV3Panel({
  open, onToggleOpen, stability, onStability, humanize, onHumanize, tags, onTags, catalog,
}: {
  open: boolean;
  onToggleOpen: () => void;
  stability: string;
  onStability: (s: string) => void;
  humanize: boolean;
  onHumanize: (v: boolean) => void;
  tags: string[];
  onTags: (tags: string[]) => void;
  catalog: ElevenLabsCatalog | null;
}) {
  const tagGroups = catalog?.v3_tags ?? [];
  const stabilityModes = catalog?.v3_stability_modes;
  // אם השרת מחזיר רשימת מצבים, נכבד אותה; אחרת 3 הברירות המובנות.
  const modes = stabilityModes?.length
    ? V3_STABILITY.filter((m) => stabilityModes.includes(m.value))
    : V3_STABILITY;

  function toggleTag(name: string) {
    onTags(tags.includes(name) ? tags.filter((t) => t !== name) : [...tags, name]);
  }
  function setGroup(group: ElevenLabsV3TagGroup, on: boolean) {
    const set = new Set(tags);
    for (const t of group.tags) { if (on) set.add(t); else set.delete(t); }
    onTags([...set]);
  }

  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', overflow: 'hidden' }}>
      <button
        type="button"
        onClick={onToggleOpen}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'var(--surface-2)', border: 'none', cursor: 'pointer', font: 'inherit', textAlign: 'start' }}
      >
        <Icon name={open ? 'chevronD' : 'chevron'} size={14} />
        <strong style={{ fontSize: 'var(--t-sm)', flex: 1 }}>יכולות Eleven v3</strong>
        {humanize && <StatusBadge tone="info" size="sm">דיבור אנושי</StatusBadge>}
      </button>

      {open && (
        <div className="stack" style={{ padding: 'var(--pad-card)', gap: 12 }}>
          {/* מצב יציבות */}
          <FormField label="מצב יציבות">
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {modes.map((m) => {
                const active = stability === m.value;
                return (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => onStability(m.value)}
                    title={m.hint}
                    style={{
                      flex: '1 0 auto', padding: '6px 10px', borderRadius: 'var(--r-2)', cursor: 'pointer',
                      border: `1px solid ${active ? 'var(--accent)' : 'var(--line)'}`,
                      background: active ? 'var(--accent-soft, var(--surface-2))' : 'var(--surface)',
                      color: active ? 'var(--accent-ink)' : 'var(--text-2)', fontWeight: active ? 600 : 500,
                      fontSize: 'var(--t-sm)', fontFamily: 'inherit', textAlign: 'start', minWidth: 130,
                    }}
                  >
                    <div>{m.label}</div>
                    <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', fontWeight: 400 }}>{m.hint}</div>
                  </button>
                );
              })}
            </div>
          </FormField>

          {/* דיבור אנושי */}
          <Toggle
            checked={humanize}
            onChange={onHumanize}
            label="דיבור אנושי — הסוכן ישלב תגיות רגש בדיבור: צחקוקים, לחישות, הפסקות — ויישמע אנושי יותר"
          />

          {/* בורר תגיות — רק כשדיבור אנושי דלוק */}
          {humanize && (
            <div className="stack" style={{ gap: 8 }}>
              <p style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', margin: 0 }}>
                התגיות מבוצעות קולית ולא נאמרות; המערכת מנחה את הסוכן להשתמש בהן במינון טבעי.
              </p>
              {tagGroups.length === 0 ? (
                <span style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>קטלוג התגיות לא נטען.</span>
              ) : (
                tagGroups.map((group) => {
                  const allOn = group.tags.every((t) => tags.includes(t));
                  return (
                    <div key={group.key} style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', padding: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <strong style={{ fontSize: 'var(--t-xs)' }}>{group.label}</strong>
                        <button type="button" className="btn ghost" style={{ height: 24, fontSize: 'var(--t-xs)' }} onClick={() => setGroup(group, !allOn)}>
                          {allOn ? 'נקה' : 'בחר הכל'}
                        </button>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {group.tags.map((name) => {
                          const on = tags.includes(name);
                          return (
                            <button
                              key={name}
                              type="button"
                              onClick={() => toggleTag(name)}
                              dir="ltr"
                              style={{
                                padding: '3px 8px', borderRadius: 'var(--r-pill)', cursor: 'pointer',
                                border: `1px solid ${on ? 'var(--accent)' : 'var(--line)'}`,
                                background: on ? 'var(--accent-soft, var(--surface-2))' : 'var(--surface)',
                                color: on ? 'var(--accent-ink)' : 'var(--text-2)',
                                fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)',
                              }}
                            >
                              [{name}]
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
              {tags.length === 0 && (
                <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
                  לא נבחרו תגיות — ייעשה שימוש בסט ברירת מחדל טבעי.
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── פאנל פרמטרים מתקדמים (מודל השיחה, קצב VAD, קול ElevenLabs) ────────────────
interface AdvancedValues {
  thinking_level: string;
  temperature: string;
  vad_silence_ms: number;
  vad_end_sensitivity: string;
  vad_start_sensitivity: string;
  vad_prefix_ms: number;
  elevenlabs_similarity: number;
  elevenlabs_speaker_boost: boolean;
}

function AdvancedParamsPanel({ open, onToggleOpen, values, onPatch, isElevenlabs, isOpenAiEngine }: {
  open: boolean;
  onToggleOpen: () => void;
  values: AdvancedValues;
  onPatch: (patch: Partial<AdvancedValues>) => void;
  isElevenlabs: boolean;
  /** ב-OpenAI Realtime חלק מהפרמטרים אינם קיימים — מסתירים אותם. */
  isOpenAiEngine?: boolean;
}) {
  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', overflow: 'hidden' }}>
      <button
        type="button"
        onClick={onToggleOpen}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'var(--surface-2)', border: 'none', cursor: 'pointer', font: 'inherit', textAlign: 'start' }}
      >
        <Icon name={open ? 'chevronD' : 'chevron'} size={14} />
        <strong style={{ fontSize: 'var(--t-sm)', flex: 1 }}>פרמטרים מתקדמים</strong>
      </button>

      {open && (
        <div className="stack" style={{ padding: 'var(--pad-card)', gap: 14 }}>
          {/* 1. מודל השיחה */}
          <div className="stack" style={{ gap: 8 }}>
            <strong style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>מודל השיחה</strong>
            {/* thinking_level נשלח רק ל-Gemini Live; ב-OpenAI הוא no-op
                מוחלט, ולכן הצגתו הייתה מטעה. */}
            <FormField label="רמת חשיבה" hint={isOpenAiEngine ? 'לא נתמך ב-OpenAI Realtime — רלוונטי רק ל-Gemini.' : undefined}>
              <FormSelect disabled={isOpenAiEngine} value={values.thinking_level} onChange={(e) => onPatch({ thinking_level: e.target.value })}>
                <option value="">ברירת מחדל — הבנה מלאה</option>
                <option value="minimal">מהיר — בלי חשיבה (עלול להבין פחות טוב)</option>
                <option value="low">חשיבה קצרה</option>
                <option value="medium">חשיבה בינונית</option>
                <option value="high">חשיבה מלאה</option>
              </FormSelect>
            </FormField>
            <FormField label="טמפרטורה (יצירתיות)" hint="יצירתיות: נמוך=צמוד לתסריט, גבוה=חופשי. ריק = ברירת מחדל.">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                  type="range" min={0} max={2} step={0.1}
                  value={values.temperature === '' ? 1 : Number(values.temperature)}
                  onChange={(e) => onPatch({ temperature: e.target.value })}
                  style={{ flex: 1 }}
                />
                <TextInput
                  type="number" min={0} max={2} step={0.1} dir="ltr"
                  value={values.temperature}
                  placeholder="—"
                  onChange={(e) => onPatch({ temperature: e.target.value })}
                  style={{ width: 90 }}
                />
              </div>
            </FormField>
          </div>

          {/* 2. קצב שיחה (VAD) */}
          <div className="stack" style={{ gap: 8, borderTop: '1px solid var(--line)', paddingTop: 10 }}>
            <strong style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>קצב שיחה (זיהוי דיבור)</strong>
            <FormField label="המתנת שקט לפני מענה (ms)" hint="נמוך=עונה מהר אך עלול לחתוך; גבוה=סבלני.">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="range" min={200} max={2000} step={50} value={values.vad_silence_ms} onChange={(e) => onPatch({ vad_silence_ms: Number(e.target.value) })} style={{ flex: 1 }} />
                <span dir="ltr" style={{ width: 56, textAlign: 'end', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sm)' }}>{values.vad_silence_ms}</span>
              </div>
            </FormField>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gap)' }}>
              <FormField label="רגישות סוף דיבור">
                <FormSelect value={values.vad_end_sensitivity} onChange={(e) => onPatch({ vad_end_sensitivity: e.target.value })}>
                  <option value="">ברירת מחדל</option>
                  <option value="END_SENSITIVITY_HIGH">גבוהה — מזהה סוף דיבור מהר</option>
                  <option value="END_SENSITIVITY_LOW">נמוכה — מחכה יותר</option>
                </FormSelect>
              </FormField>
              <FormField label="רגישות תחילת דיבור">
                <FormSelect value={values.vad_start_sensitivity} onChange={(e) => onPatch({ vad_start_sensitivity: e.target.value })}>
                  <option value="">ברירת מחדל</option>
                  <option value="START_SENSITIVITY_HIGH">גבוהה — קטיעה קלה של הסוכן</option>
                  <option value="START_SENSITIVITY_LOW">נמוכה</option>
                </FormSelect>
              </FormField>
            </div>
            <FormField label="ריפוד תחילת דיבור (ms)">
              <TextInput type="number" min={20} max={500} dir="ltr" value={String(values.vad_prefix_ms)} onChange={(e) => onPatch({ vad_prefix_ms: Number(e.target.value) || 0 })} />
            </FormField>
          </div>

          {/* 3. קול ElevenLabs — רק כשזה המנוע */}
          {isElevenlabs && (
            <div className="stack" style={{ gap: 8, borderTop: '1px solid var(--line)', paddingTop: 10 }}>
              <strong style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>קול ElevenLabs</strong>
              <FormField label="דמיון לקול המקור">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input type="range" min={0} max={1} step={0.05} value={values.elevenlabs_similarity} onChange={(e) => onPatch({ elevenlabs_similarity: Number(e.target.value) })} style={{ flex: 1 }} />
                  <span dir="ltr" style={{ width: 48, textAlign: 'end', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sm)' }}>{values.elevenlabs_similarity.toFixed(2)}</span>
                </div>
              </FormField>
              <Toggle checked={values.elevenlabs_speaker_boost} onChange={(v) => onPatch({ elevenlabs_speaker_boost: v })} label="הגברת נוכחות קול" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Detail drawer (overview / assignments / sessions / memories) ──────────────
function VoiceAgentStudio({
  agentId, nonce, tools, libraryTools, flows, tenants, tenantName, onClose, onEdit, onDelete, onMetaChanged, onAgentPatched,
}: {
  agentId: number | null;
  nonce: number;
  tools: VoiceToolCatalogItem[];
  libraryTools: VoiceTool[];
  flows: VoiceFlowRow[];
  tenants: TenantLite[];
  tenantName: (id?: number | null) => string | null;
  onClose: () => void;
  onEdit: (a: VoiceAgentRow) => void;
  onDelete: (a: VoiceAgentRow) => void;
  onMetaChanged: () => void;
  onAgentPatched: () => void;
}) {
  const [agent, setAgent] = useState<VoiceAgentRow | null>(null);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [dialOpen, setDialOpen] = useState(false);
  const [dialTo, setDialTo] = useState('');
  const [dialing, setDialing] = useState(false);
  const [testCallOpen, setTestCallOpen] = useState(false);

  useEffect(() => {
    if (agentId == null) { setAgent(null); return; }
    // nonce מתעדכן אחרי עריכה/שיוך — טוענים מחדש בלי לאפס את הטאב.
    setAgent((a) => { setTab((t) => (a == null ? 'overview' : t)); return a; });
    setLoading(true);
    // Guard against out-of-order resolution: clicking A then B must not let A's
    // late response render over B (which would mis-target edit/delete).
    let active = true;
    void superApi.voiceAgent(agentId)
      .then((a) => { if (active) setAgent(a); })
      .catch(() => { if (active) toast('שגיאה בטעינת הסוכן', 'danger'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [agentId, nonce]);

  const isGlobal = agent != null && agent.tenant_id == null;
  // סוכן שמתארח ב-ElevenLabs: זרימה/כלים/רשומות מנוהלים אצלם.
  const hostedAgent = (agent?.settings as { agent_host?: string } | undefined)?.agent_host === 'elevenlabs';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* כותרת הסטודיו */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px var(--gap)', borderBottom: '1px solid var(--line)', flexWrap: 'wrap', flexShrink: 0 }}>
        <button type="button" className="btn ghost" onClick={onClose}>← חזרה לרשימה</button>
        <strong style={{ fontSize: 'var(--t-md)' }}>{agent?.name ?? 'טוען…'}</strong>
        {agent && <StatusBadge tone={STATUS_TONE[agent.status]} size="sm">{STATUS_LABEL[agent.status]}</StatusBadge>}
        <div style={{ flex: 1 }} />
        {agent && <button type="button" className="btn" onClick={() => onEdit(agent)}>ערוך הגדרות</button>}
        {agent && (
          <button type="button" className="btn" onClick={() => setTestCallOpen(true)} disabled={isGlobal}
            title={isGlobal ? 'לסוכן גלובלי אין שלוחות — שייך אותו למרכזיה' : undefined}>
            🎧 שיחת נסיון
          </button>
        )}
        {agent && (agent.direction === 'outbound' || agent.direction === 'both') && agent.status === 'active' && (
          <button type="button" className="btn primary" onClick={() => { setDialTo(''); setDialOpen(true); }}>📞 חייג</button>
        )}
        {agent && <button type="button" className="btn danger" onClick={() => onDelete(agent)}>מחק</button>}
      </div>

      {loading || !agent ? (
        <Card>טוען…</Card>
      ) : (
        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          {/* ניווט צד מקובץ: בנייה / חיבורים / ניטור */}
          <nav aria-label="ניווט הסטודיו" style={{ width: 192, flexShrink: 0, borderInlineEnd: '1px solid var(--line)', overflowY: 'auto', padding: '10px 8px', background: 'var(--surface)' }}>
            {([
              { title: null, items: [{ id: 'overview', label: 'סקירה', icon: '🏠', badge: null }] },
              {
                // סוכן מתארח: הזרימה, הכלים והרשומות מנוהלים אצל ElevenLabs
                // ולא מזינים את הצנרת שלנו — הצגתם הייתה מטעה.
                title: 'בנייה', items: hostedAgent ? [] : [
                  { id: 'flow', label: 'זרימת שיחה', icon: '🧭', badge: agent.flow_id ? '✓' : null },
                  { id: 'tools', label: 'כלי API', icon: '🔌', badge: (agent.tool_ids?.length ?? 0) > 0 ? String(agent.tool_ids!.length) : null },
                  { id: 'records', label: 'רשומות', icon: '🗃️', badge: null },
                  { id: 'knowledge', label: 'מאגר ידע', icon: '📚', badge: agent.knowledge_base_id ? '✓' : null },
                ],
              },
              {
                title: 'חיבורים', items: [
                  ...(isGlobal ? [{ id: 'assignments', label: 'שיוך למרכזיות', icon: '🏢', badge: agent.assignments?.length ? String(agent.assignments.length) : null }] : []),
                  { id: 'numbers', label: 'מספרים', icon: '📞', badge: null },
                ],
              },
              {
                // "שיחה חיה" מנטרת את הסשן בגשר שלנו. בסוכן מתארח אין סשן
                // כזה — המסך היה נשאר ריק לנצח, ולכן הוא מוסתר.
                title: 'ניטור', items: [
                  ...(hostedAgent ? [] : [{ id: 'live', label: 'שיחה חיה', icon: '🎧', badge: null }]),
                  { id: 'sessions', label: 'היסטוריית שיחות', icon: '🗂️', badge: null },
                  { id: 'memories', label: 'זיכרון לקוחות', icon: '🧠', badge: null },
                  { id: 'stats', label: 'סטטיסטיקות', icon: '📊', badge: null },
                ],
              },
            ] as { title: string | null; items: { id: string; label: string; icon: string; badge: string | null }[] }[]).map((group, gi) => (
              <div key={gi} style={{ marginTop: gi === 0 ? 0 : 12 }}>
                {group.title && (
                  <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', padding: '0 8px 4px', fontWeight: 600 }}>{group.title}</div>
                )}
                {group.items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setTab(item.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                      padding: '7px 8px', border: 'none', borderRadius: 'var(--r-2)', cursor: 'pointer',
                      textAlign: 'start', fontSize: 'var(--t-sm)',
                      background: tab === item.id ? 'color-mix(in oklch, var(--accent) 12%, var(--surface))' : 'transparent',
                      color: tab === item.id ? 'var(--accent)' : 'var(--text)',
                      fontWeight: tab === item.id ? 700 : 400,
                    }}
                  >
                    <span aria-hidden style={{ fontSize: 14 }}>{item.icon}</span>
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span>
                    {item.badge && (
                      <span style={{
                        fontSize: 'var(--t-xs)', fontWeight: 700, lineHeight: 1, padding: '3px 6px',
                        borderRadius: 999, background: 'color-mix(in oklch, var(--accent) 14%, var(--surface))', color: 'var(--accent)',
                      }}>{item.badge}</span>
                    )}
                  </button>
                ))}
              </div>
            ))}
          </nav>

          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0 }}>

          {tab === 'flow' && (
            <StudioFlowTab
              agent={agent}
              flows={flows}
              tenants={tenants}
              onAgentPatched={onAgentPatched}
              onMetaChanged={onMetaChanged}
            />
          )}
          {tab === 'tools' && (
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 'var(--gap)' }}>
              <StudioToolsTab
                agent={agent}
                libraryTools={libraryTools}
                tenants={tenants}
                onAgentPatched={onAgentPatched}
                onMetaChanged={onMetaChanged}
              />
            </div>
          )}

          {tab !== 'flow' && tab !== 'tools' && (
          <div className="stack" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 'var(--gap)' }}>

          {tab === 'overview' && (
            <>
              {/* סוכן מתארח נראה במסך כמו כל סוכן אחר, אבל מתנהג אחרת לגמרי.
                  הכרטיס הזה אומר את זה מפורשות ומונה מה חסום ולמה. */}
              {hostedAgent && (
                <Card title="☁ סוכן מתארח ב-ElevenLabs">
                  <div className="stack" style={{ fontSize: 'var(--t-sm)', gap: 10 }}>
                    <p style={{ margin: 0 }}>
                      כל השיחה — זיהוי הדיבור, המודל והקול — מתנהלת אצל ElevenLabs.
                      אנחנו מנתבים אליהם את המספר, דוחפים את ההנחיות והכלים, ומושכים
                      את התמלילים בחזרה. <strong>ההגדרות כאן הן מקור האמת</strong>:
                      כל שמירה דורשת סנכרון מחדש כדי להגיע אליהם.
                    </p>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>לא זמין בסוכן כזה:</div>
                      <ul style={{ margin: 0, paddingInlineStart: 18, color: 'var(--muted)' }}>
                        {HOSTED_UNAVAILABLE.map((x) => (
                          <li key={x.label}><strong>{x.label}</strong> — {x.why}</li>
                        ))}
                      </ul>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button type="button" className="btn ghost" onClick={() => onEdit(agent)}>
                        ⬆ סנכרן ל-ElevenLabs
                      </button>
                      <button type="button" className="btn ghost" onClick={() => setTab('sessions')}>
                        🗂️ תמלילי שיחות
                      </button>
                    </div>
                  </div>
                </Card>
              )}

              {/* לסוכן מתארח אין flow/כלים אצלנו — הבאנר היה מתלונן לנצח. */}
              {(agent.status === 'draft' || (!hostedAgent && (!agent.flow_id || !(agent.tool_ids?.length)))) && (
                <Card title="השלמת הגדרה">
                  <div className="stack" style={{ fontSize: 'var(--t-sm)' }}>
                    {agent.status === 'draft' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>🟡 הסוכן במצב טיוטה — לא עונה לשיחות.</span>
                        <button type="button" className="btn ghost" onClick={() => onEdit(agent)}>הפעל בעריכת הגדרות</button>
                      </div>
                    )}
                    {!hostedAgent && !agent.flow_id && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>🧭 אין זרימת שיחה — הסוכן ינהל שיחה חופשית לפי ההנחיות.</span>
                        <button type="button" className="btn ghost" onClick={() => setTab('flow')}>הגדר זרימה</button>
                      </div>
                    )}
                    {!hostedAgent && !(agent.tool_ids?.length) && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>🔌 אין כלי API — הסוכן לא יכול לבדוק מלאי/הזמנות בזמן שיחה.</span>
                        <button type="button" className="btn ghost" onClick={() => setTab('tools')}>צרף כלים</button>
                      </div>
                    )}
                  </div>
                </Card>
              )}
              <OverviewTab agent={agent} tools={tools} tenantName={tenantName} />
            </>
          )}
          {tab === 'assignments' && isGlobal && (
            <AssignmentsTab
              agent={agent}
              tenants={tenants}
              onSaved={(updated) => {
                // Keep the open drawer in sync with the server: refresh the local
                // agent's assignments so the overview count updates immediately.
                setAgent((a) => (a ? { ...a, assignments: updated } : a));
                onAgentPatched();
              }}
            />
          )}
          {tab === 'numbers' && <><NumbersTab agent={agent} /><PaymentCredentialCard agent={agent} /></>}
          {tab === 'live' && <LiveDebugTab agentId={agent.id} />}
          {tab === 'sessions' && <SessionsTab agentId={agent.id} />}
          {tab === 'memories' && <MemoriesTab agentId={agent.id} tenantName={tenantName} />}
          {tab === 'records' && <StudioRecordsTab agentId={agent.id} />}
          {tab === 'knowledge' && <StudioKnowledgeTab agentId={agent.id} knowledgeBaseId={agent.knowledge_base_id} />}
          {tab === 'stats' && <StudioStatsTab agentId={agent.id} />}

          </div>
          )}

          </div>

          <Modal
            isOpen={dialOpen}
            onClose={() => !dialing && setDialOpen(false)}
            title={`חיוג יוצא — ${agent.name}`}
            footer={
              <>
                <button type="button" className="btn" onClick={() => setDialOpen(false)} disabled={dialing}>ביטול</button>
                <button
                  type="button"
                  className="btn primary"
                  disabled={dialing || !dialTo.trim()}
                  onClick={async () => {
                    setDialing(true);
                    try {
                      const res = await superApi.dialVoiceAgent(agent.id, dialTo.trim(), agent.tenant_id ?? undefined);
                      if (res?.success) {
                        toast('השיחה יוצאת — הטלפון אמור לצלצל', 'ok');
                        setDialOpen(false);
                      } else {
                        toast(res?.message || 'החיוג נכשל', 'danger');
                      }
                    } catch (e: unknown) {
                      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה בחיוג';
                      toast(msg, 'danger');
                    } finally {
                      setDialing(false);
                    }
                  }}
                >
                  {dialing ? 'מחייג…' : 'חייג עכשיו'}
                </button>
              </>
            }
          >
            <div className="stack">
              <FormField label="מספר טלפון" hint="פורמט ישראלי, לדוגמה 0502298318">
                <TextInput
                  value={dialTo}
                  onChange={(e) => setDialTo(e.target.value)}
                  placeholder="0502298318"
                  dir="ltr"
                  autoFocus
                />
              </FormField>
              <p style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)', margin: 0 }}>
                הסוכן יחייג למספר, ובמענה ינהל שיחה קולית חיה (Gemini Live).
              </p>
            </div>
          </Modal>

          {testCallOpen && (
            <TestCallModal agent={agent} onClose={() => setTestCallOpen(false)} />
          )}
        </div>
      )}
    </div>
  );
}

// ── טאב מספרים: שיוך DIDs בקליק (בלי טקסט חופשי) ──────────────────────────────
/**
 * טאב הזרימה בסטודיו: הבונה מוטמע ישירות בסוכן; אין זרימה → בחירה
 * מהקיימות (בשיוך של הסוכן) או יצירה מתבנית — בלי לעזוב את הסוכן.
 */
function StudioFlowTab({ agent, flows, tenants, onAgentPatched, onMetaChanged }: {
  agent: VoiceAgentRow;
  flows: VoiceFlowRow[];
  tenants: TenantLite[];
  onAgentPatched: () => void;
  onMetaChanged: () => void;
}) {
  const [pickId, setPickId] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const visible = flows.filter((f) => f.tenant_id == null || f.tenant_id === agent.tenant_id);

  async function attach(flowId: number | null) {
    setSaving(true);
    try {
      await superApi.updateVoiceAgent(agent.id, { flow_id: flowId });
      toast(flowId ? 'הזרימה שויכה לסוכן' : 'הזרימה נותקה מהסוכן', 'ok');
      onAgentPatched();
    } catch {
      toast('שגיאה בעדכון הזרימה', 'danger');
    } finally {
      setSaving(false);
    }
  }

  if (agent.flow_id) {
    const current = flows.find((f) => f.id === agent.flow_id);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px var(--gap)', flexShrink: 0, fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>
          זרימת הסוכן: <strong style={{ color: 'var(--text)' }}>{current?.name ?? `#${agent.flow_id}`}</strong>
          <button type="button" className="btn ghost" disabled={saving} onClick={() => void attach(null)}>נתק זרימה</button>
        </div>
        <div style={{ flex: 1, minHeight: 0 }}>
          <FlowBuilder flowId={agent.flow_id} onBack={onAgentPatched} />
        </div>
      </div>
    );
  }

  return (
    <div className="stack" style={{ padding: 'var(--gap)', maxWidth: 560 }}>
      <Card title="לסוכן אין זרימה">
        <div className="stack">
          <p style={{ margin: 0, fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>
            בלי זרימה הסוכן מנהל שיחה חופשית לפי ההנחיות. זרימה מובנית מובילה
            את השיחה בשלבים (פיצולי IF, איסוף פרטים, שליחת API בסיום).
          </p>
          <FormField label="שייך זרימה קיימת">
            <FormSelect value={pickId} onChange={(e) => setPickId(e.target.value)}>
              <option value="">בחר זרימה…</option>
              {visible.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </FormSelect>
          </FormField>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn primary" disabled={!pickId || saving} onClick={() => void attach(Number(pickId))}>
              שייך לסוכן
            </button>
            <button type="button" className="btn" onClick={() => setCreateOpen(true)}>+ זרימה חדשה מתבנית</button>
          </div>
        </div>
      </Card>
      {createOpen && (
        <FlowMetaForm
          mode="create"
          tenants={tenants}
          onClose={() => setCreateOpen(false)}
          onSaved={(flow) => {
            setCreateOpen(false);
            onMetaChanged();
            if (flow) void attach(flow.id);
          }}
        />
      )}
    </div>
  );
}

/**
 * טאב הכלים בסטודיו: צירוף/ניתוק כלי ספרייה לסוכן + יצירה ועריכה במקום.
 * כלי שנוצר כאן מצורף לסוכן אוטומטית.
 */
function StudioToolsTab({ agent, libraryTools, tenants, onAgentPatched, onMetaChanged }: {
  agent: VoiceAgentRow;
  libraryTools: VoiceTool[];
  tenants: TenantLite[];
  onAgentPatched: () => void;
  onMetaChanged: () => void;
}) {
  const [createOpen, setCreateOpen] = useState(false);
  const [editTool, setEditTool] = useState<VoiceTool | null>(null);
  const [saving, setSaving] = useState(false);
  const visible = libraryTools.filter((t) => t.tenant_id == null || t.tenant_id === agent.tenant_id);
  const attached = new Set(agent.tool_ids ?? []);

  async function setToolIds(ids: number[]) {
    setSaving(true);
    try {
      await superApi.updateVoiceAgent(agent.id, { tool_ids: ids });
      onAgentPatched();
    } catch {
      toast('שגיאה בעדכון הכלים', 'danger');
    } finally {
      setSaving(false);
    }
  }

  function toggle(id: number) {
    const ids = attached.has(id)
      ? [...attached].filter((x) => x !== id)
      : [...attached, id];
    void setToolIds(ids);
  }

  return (
    <div className="stack" style={{ maxWidth: 720 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <p style={{ margin: 0, fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>
          כלי שמצורף לסוכן זמין לו בשיחה (למשל בדיקת מלאי). בזרימה אפשר לצמצם
          כלים פר-שלב — ברירת המחדל: כל כלי הסוכן מותרים.
        </p>
        <button type="button" className="btn primary" onClick={() => setCreateOpen(true)}>+ כלי חדש</button>
      </div>
      {visible.length === 0 ? (
        <Card><EmptyState title="אין כלי API בשיוך הזה" description="צור כלי חדש — הוא יצורף לסוכן אוטומטית." /></Card>
      ) : (
        visible.map((t) => (
          <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: attached.has(t.id) ? 'color-mix(in oklch, var(--accent) 6%, var(--surface))' : 'var(--surface)' }}>
            <Toggle checked={attached.has(t.id)} onChange={() => toggle(t.id)} disabled={saving} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 'var(--t-sm)' }}>{t.name}</div>
              <div dir="ltr" style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'end' }}>
                {t.method} {t.url}
              </div>
            </div>
            <button type="button" className="btn" onClick={() => setEditTool(t)}>ערוך</button>
          </div>
        ))
      )}

      {createOpen && (
        <VoiceToolForm
          mode="create"
          tenants={tenants}
          onClose={() => setCreateOpen(false)}
          onSaved={(saved) => {
            setCreateOpen(false);
            onMetaChanged();
            // צירוף אוטומטי של הכלי החדש לסוכן.
            if (saved?.id) void setToolIds([...attached, saved.id]);
          }}
        />
      )}
      {editTool && (
        <VoiceToolForm
          mode="edit"
          tool={editTool}
          tenants={tenants}
          onClose={() => setEditTool(null)}
          onSaved={() => { setEditTool(null); onMetaChanged(); }}
        />
      )}
    </div>
  );
}

/** פרטי סליקה לתשלום בשיחה — מוצג בטאב מספרים. secrets write-only. */
function PaymentCredentialCard({ agent }: { agent: VoiceAgentRow }) {
  const [cred, setCred] = useState<VoicePaymentCredential | null>(null);
  const method = (agent.settings as { payment?: { method?: string } } | undefined)?.payment?.method ?? 'none';
  const provider = method === 'dtmf' ? 'cardcom' : ((agent.settings as { payment?: { provider?: string } } | undefined)?.payment?.provider ?? 'green_invoice');
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [extra, setExtra] = useState<Record<string, string>>({ terminal_number: '', api_name: '', api_password: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void superApi.voicePaymentCredential(agent.id).then(setCred).catch(() => setCred({ configured: false }));
  }, [agent.id]);

  if (method === 'none') return null;

  return (
    <Card title="פרטי סליקה" style={{ marginTop: 'var(--gap)' }}>
      <div className="stack">
        <p style={{ margin: 0, fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>
          ספק: <strong>{provider === 'cardcom' ? 'CardCom' : provider === 'generic' ? 'ספק מותאם' : 'חשבונית ירוקה'}</strong>
          {cred?.configured ? ' · מוגדר ✓' : ' · לא מוגדר'}
          {cred?.last_verified_at ? ' · אומת' : ''}
        </p>
        {provider === 'cardcom' ? (
          <>
            <FormField label="מספר מסוף (Terminal)"><TextInput value={extra.terminal_number} dir="ltr" onChange={(e) => setExtra({ ...extra, terminal_number: e.target.value })} /></FormField>
            <FormField label="API Name"><TextInput value={extra.api_name} dir="ltr" onChange={(e) => setExtra({ ...extra, api_name: e.target.value })} /></FormField>
            <FormField label="API Password" hint={cred?.configured ? 'ריק = לא לשנות את הקיים' : undefined}><TextInput type="password" value={extra.api_password} dir="ltr" onChange={(e) => setExtra({ ...extra, api_password: e.target.value })} /></FormField>
          </>
        ) : provider === 'green_invoice' ? (
          <>
            <FormField label="API Key (חשבונית ירוקה)"><TextInput value={apiKey} dir="ltr" onChange={(e) => setApiKey(e.target.value)} /></FormField>
            <FormField label="API Secret" hint={cred?.configured ? 'ריק = לא לשנות את הקיים' : undefined}><TextInput type="password" value={apiSecret} dir="ltr" onChange={(e) => setApiSecret(e.target.value)} /></FormField>
          </>
        ) : (
          <p style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)', margin: 0 }}>
            ספק מותאם — הגדר את כתובת הכלי בטופס הסוכן. הפנה את ה-webhook של הספק אל:
            {cred?.callback_url && <code dir="ltr" style={{ display: 'block', marginTop: 4, fontSize: 'var(--t-xs)' }}>{cred.callback_url}</code>}
          </p>
        )}
        <div>
          <button
            type="button" className="btn primary" disabled={saving}
            onClick={async () => {
              setSaving(true);
              try {
                const body: { provider: string; api_key?: string; api_secret?: string; extra?: Record<string, unknown> } = { provider };
                if (provider === 'cardcom') body.extra = extra;
                else { if (apiKey) body.api_key = apiKey; if (apiSecret) body.api_secret = apiSecret; }
                setCred(await superApi.saveVoicePaymentCredential(agent.id, body));
                toast('פרטי הסליקה נשמרו', 'ok');
                setApiKey(''); setApiSecret(''); setExtra({ ...extra, api_password: '' });
              } catch (e: unknown) {
                toast((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'השמירה נכשלה', 'danger');
              } finally { setSaving(false); }
            }}
          >{saving ? 'שומר…' : 'שמור פרטי סליקה'}</button>
        </div>
      </div>
    </Card>
  );
}

function NumbersTab({ agent }: { agent: VoiceAgentRow }) {
  const [numbers, setNumbers] = useState<VoiceAgentNumber[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // אישור לפני צירוף מספר שמנותב כרגע ליעד אחר (תור/IVR).
  const [confirmReroute, setConfirmReroute] = useState<VoiceAgentNumber | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    void superApi.voiceAgentNumbers(agent.id)
      .then((rows) => {
        setNumbers(rows);
        setSelected(new Set(rows.filter((r) => r.assigned_to_this).map((r) => r.id)));
      })
      .catch(() => toast('שגיאה בטעינת המספרים', 'danger'))
      .finally(() => setLoading(false));
  }, [agent.id]);

  useEffect(() => { load(); }, [load]);

  // route_type שאינו ריק ואינו voiceagent = מנותב כרגע ליעד אחר (תור/IVR וכו').
  const isRerouted = (n: VoiceAgentNumber) => !!n.route_type && n.route_type !== 'voiceagent';

  function toggle(n: VoiceAgentNumber) {
    if (n.assigned_to_other_agent) return; // נעול
    const already = selected.has(n.id);
    if (!already && isRerouted(n)) { setConfirmReroute(n); return; }
    setSelected((prev) => {
      const next = new Set(prev);
      if (already) next.delete(n.id); else next.add(n.id);
      return next;
    });
  }

  function confirmInclude(n: VoiceAgentNumber) {
    setSelected((prev) => new Set(prev).add(n.id));
    setConfirmReroute(null);
  }

  async function save() {
    setSaving(true);
    try {
      const rows = await superApi.setVoiceAgentNumbers(agent.id, [...selected]);
      setNumbers(rows);
      setSelected(new Set(rows.filter((r) => r.assigned_to_this).map((r) => r.id)));
      toast('שיוך המספרים נשמר', 'ok');
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה בשמירת השיוך';
      toast(msg, 'danger');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Card>טוען…</Card>;
  if (numbers.length === 0) return <EmptyState title="אין מספרים" description="אין מספרים זמינים לשיוך לסוכן זה." />;

  return (
    <div className="stack">
      <p style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)', margin: 0 }}>
        סמן מספרים כדי לנתב שיחות נכנסות אליהם לסוכן זה. מספר משויך לסוכן אחד בלבד.
      </p>
      <div className="stack" style={{ maxHeight: 360, overflow: 'auto', gap: 6 }}>
        {numbers.map((n) => {
          const disabled = n.assigned_to_other_agent;
          const rerouted = isRerouted(n);
          return (
            <label
              key={n.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                borderRadius: 'var(--r-2)', border: '1px solid var(--line)',
                cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
              }}
            >
              <input type="checkbox" checked={selected.has(n.id)} disabled={disabled} onChange={() => toggle(n)} />
              <span dir="ltr" style={{ fontSize: 'var(--t-sm)', fontWeight: 600, flex: 1 }}>{n.number}</span>
              {disabled && <StatusBadge tone="neutral" size="sm">משויך לסוכן אחר</StatusBadge>}
              {!disabled && rerouted && <StatusBadge tone="warn" size="sm">מנותב כרגע: {n.route_type}</StatusBadge>}
            </label>
          );
        })}
      </div>
      <button type="button" className="btn primary" onClick={save} disabled={saving}>
        {saving ? 'שומר…' : 'שמור שיוך'}
      </button>

      <ConfirmDialog
        isOpen={!!confirmReroute}
        onClose={() => setConfirmReroute(null)}
        onConfirm={() => confirmReroute && confirmInclude(confirmReroute)}
        title="שינוי ניתוב המספר"
        message={confirmReroute ? `המספר ${confirmReroute.number} מנותב כרגע ל-${confirmReroute.route_type}. לצרף אותו לסוכן ולנתק את הניתוב הקיים?` : ''}
        confirmLabel="צרף בכל זאת"
        tone="warn"
      />
    </div>
  );
}

// ── טאב שיחה חיה (debug): polling כל 2s ──────────────────────────────────────
function LiveDebugTab({ agentId }: { agentId: number }) {
  const [calls, setCalls] = useState<VoiceLiveCall[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [, force] = useState(0);

  useEffect(() => {
    let active = true;
    const poll = () => {
      void superApi.voiceAgentLive(agentId)
        .then((c) => { if (active) { setCalls(c); setLoaded(true); } })
        .catch(() => { /* non-fatal — polling ימשיך */ });
    };
    poll();
    const h = setInterval(poll, 2000);
    const tick = setInterval(() => force((n) => n + 1), 1000);
    return () => { active = false; clearInterval(h); clearInterval(tick); };
  }, [agentId]);

  if (!loaded) return <Card>טוען…</Card>;
  if (calls.length === 0) return <EmptyState title="אין שיחות פעילות" description="שיחות חיות של הסוכן יופיעו כאן." />;

  return (
    <div className="stack">
      {calls.map((c) => {
        const stage = c.stage_history?.length ? (c.stage_history[c.stage_history.length - 1].key ?? c.stage_history[c.stage_history.length - 1].stage) : c.current_stage_id;
        const vars = Object.entries(c.variables ?? {}).filter(([, v]) => v != null && v !== '');
        return (
          <Card key={c.id}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
              <span dir="ltr" style={{ fontSize: 'var(--t-sm)', fontWeight: 600 }}>{c.caller_number ?? '—'}</span>
              <StatusBadge tone="ok" dot size="sm">{liveElapsed(c.started_at)}</StatusBadge>
            </div>
            {stage && (
              <div style={{ fontSize: 'var(--t-xs)', marginTop: 4 }}>
                <span style={{ color: 'var(--muted)' }}>שלב נוכחי: </span><span dir="ltr">{stage}</span>
              </div>
            )}
            {vars.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                {vars.map(([k, v]) => (
                  <span key={k} style={{
                    display: 'inline-flex', gap: 4, fontSize: 'var(--t-xs)', padding: '2px 6px',
                    borderRadius: 'var(--r-pill)', background: 'var(--surface-2)', border: '1px solid var(--line)',
                  }}>
                    <span dir="ltr" style={{ color: 'var(--muted)' }}>{k}:</span>
                    <span dir="ltr">{String(v)}</span>
                  </span>
                ))}
              </div>
            )}
            {c.stage_history && c.stage_history.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4, marginTop: 6 }}>
                {c.stage_history.map((h, i) => (
                  <span key={i} dir="ltr" style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
                    {h.key ?? h.stage ?? '—'}{i < c.stage_history!.length - 1 ? ' → ' : ''}
                  </span>
                ))}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function liveElapsed(iso?: string | null): string {
  if (!iso) return '—';
  const start = new Date(iso).getTime();
  if (Number.isNaN(start)) return '—';
  const sec = Math.max(0, Math.round((Date.now() - start) / 1000));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// ── שיחת נסיון: הסופטפון של השלוחה בדפדפן מצלצל ────────────────────────────────
function TestCallModal({ agent, onClose }: { agent: VoiceAgentRow; onClose: () => void }) {
  const [extensions, setExtensions] = useState<VoiceAgentExtension[]>([]);
  const [extId, setExtId] = useState('');
  const [loading, setLoading] = useState(true);
  const [calling, setCalling] = useState(false);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    setLoading(true);
    void superApi.voiceAgentExtensions(agent.id)
      .then(setExtensions)
      .catch(() => toast('שגיאה בטעינת השלוחות', 'danger'))
      .finally(() => setLoading(false));
  }, [agent.id]);

  async function call() {
    if (!extId) { toast('יש לבחור שלוחה', 'danger'); return; }
    setCalling(true);
    try {
      const res = await superApi.voiceAgentTestCall(agent.id, Number(extId), agent.tenant_id ?? undefined);
      if (res.success) {
        toast('השיחה יוצאת — הסופטפון בדפדפן אמור לצלצל', 'ok');
        setStarted(true);
      } else {
        toast(res.message || 'שיחת הנסיון נכשלה', 'danger');
      }
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה בשיחת הנסיון';
      toast(msg, 'danger');
    } finally {
      setCalling(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`שיחת נסיון — ${agent.name}`}
      size={started ? 'lg' : 'md'}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={calling}>סגור</button>
          <button type="button" className="btn primary" onClick={call} disabled={calling || !extId}>
            {calling ? 'מתקשר…' : 'התקשר אליי לדפדפן'}
          </button>
        </>
      }
    >
      <div className="stack">
        <FormField label="שלוחה" hint="הסופטפון בדפדפן יצלצל — ענה ודבר עם הסוכן">
          {loading ? (
            <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>טוען שלוחות…</div>
          ) : extensions.length === 0 ? (
            <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>אין שלוחות זמינות למרכזייה זו.</div>
          ) : (
            <FormSelect value={extId} onChange={(e) => setExtId(e.target.value)} autoFocus>
              <option value="">— בחר שלוחה —</option>
              {extensions.map((x) => (
                <option key={x.id} value={x.id}>{x.ext}{x.name ? ` — ${x.name}` : ''}</option>
              ))}
            </FormSelect>
          )}
        </FormField>

        {started && (
          <div style={{ borderTop: '1px solid var(--line)', paddingTop: 12 }}>
            <strong style={{ fontSize: 'var(--t-sm)' }}>שיחה חיה (debug)</strong>
            <div style={{ marginTop: 8 }}>
              <LiveDebugTab agentId={agent.id} />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function OverviewTab({ agent, tools, tenantName }: { agent: VoiceAgentRow; tools: VoiceToolCatalogItem[]; tenantName: (id?: number | null) => string | null }) {
  const toolLabel = (name: string) => tools.find((t) => t.name === name)?.name ?? name;
  return (
    <div className="stack">
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 16px', fontSize: 'var(--t-sm)' }}>
        <span style={{ color: 'var(--muted)' }}>סטטוס:</span>
        <span><StatusBadge tone={STATUS_TONE[agent.status] ?? 'neutral'} dot size="sm">{STATUS_LABEL[agent.status] ?? agent.status}</StatusBadge></span>
        <span style={{ color: 'var(--muted)' }}>שיוך:</span>
        <span>{agent.tenant_id ? tenantName(agent.tenant_id) : `גלובלי · ${agent.assignments?.length ?? 0} מרכזיות`}</span>
        <span style={{ color: 'var(--muted)' }}>כיוון:</span>
        <span>{DIRECTION_LABEL[agent.direction] ?? agent.direction}</span>
        <span style={{ color: 'var(--muted)' }}>מודל:</span>
        <span dir="ltr">{agent.gemini_model ?? '—'}</span>
        <span style={{ color: 'var(--muted)' }}>קול / שפה:</span>
        <span dir="ltr">{agent.voice_name ?? '—'} · {agent.language ?? '—'}</span>
        <span style={{ color: 'var(--muted)' }}>משך מקס׳:</span>
        <span dir="ltr">{agent.max_call_seconds ? `${agent.max_call_seconds}s` : 'ברירת מחדל'}</span>
        <span style={{ color: 'var(--muted)' }}>זיכרון:</span>
        <span>{agent.memory_enabled ? 'פעיל' : 'כבוי'}</span>
      </div>

      {agent.greeting_text && (
        <div>
          <strong style={{ fontSize: 'var(--t-sm)' }}>הודעת פתיחה:</strong>
          <p style={{ fontSize: 'var(--t-sm)', margin: '4px 0 0' }}>{agent.greeting_text}</p>
        </div>
      )}
      <div>
        <strong style={{ fontSize: 'var(--t-sm)' }}>הנחיות מערכת:</strong>
        <p style={{ fontSize: 'var(--t-sm)', margin: '4px 0 0', whiteSpace: 'pre-wrap', color: 'var(--text-2)' }}>
          {agent.system_prompt || '—'}
        </p>
      </div>
      <div>
        <strong style={{ fontSize: 'var(--t-sm)' }}>פונקציות:</strong>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
          {(agent.enabled_tools ?? []).length === 0 && (agent.custom_tools ?? []).length === 0
            ? <span style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>אין</span>
            : <>
              {(agent.enabled_tools ?? []).map((t) => <StatusBadge key={t} tone="neutral" size="sm">{toolLabel(t)}</StatusBadge>)}
              {(agent.custom_tools ?? []).map((t) => <StatusBadge key={t.name} tone="info" size="sm">{t.name} ↗</StatusBadge>)}
            </>}
        </div>
      </div>
    </div>
  );
}

function AssignmentsTab({ agent, tenants, onSaved }: { agent: VoiceAgentRow; tenants: TenantLite[]; onSaved: (updated: VoiceAssignment[]) => void }) {
  const [assignments, setAssignments] = useState<VoiceAssignment[]>(agent.assignments ?? []);
  const [saving, setSaving] = useState(false);

  const assigned = new Set(assignments.map((a) => a.tenant_id));

  function toggle(tenantId: number) {
    setAssignments((prev) =>
      assigned.has(tenantId)
        ? prev.filter((a) => a.tenant_id !== tenantId)
        : [...prev, { tenant_id: tenantId, direction_override: null }],
    );
  }
  function setOverride(tenantId: number, val: string) {
    setAssignments((prev) => prev.map((a) => (a.tenant_id === tenantId ? { ...a, direction_override: (val || null) as VoiceDirection | null } : a)));
  }

  async function save() {
    setSaving(true);
    try {
      const saved = await superApi.syncVoiceAssignments(agent.id, assignments);
      // Adopt the server-canonical set (carries assigned ids) locally + upward.
      const canonical = saved.length ? saved : assignments;
      setAssignments(canonical);
      toast('השיוך נשמר', 'ok');
      onSaved(canonical);
    } catch {
      toast('שגיאה בשמירת השיוך', 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="stack">
      <p style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)', margin: 0 }}>
        סוכן גלובלי משרת רק מרכזיות שמשויכות אליו. אפשר לצמצם כיוון פר-מרכזייה. הזיכרון לעולם לא משותף בין מרכזיות.
      </p>
      <div className="stack" style={{ maxHeight: 360, overflow: 'auto' }}>
        {tenants.map((t) => {
          const on = assigned.has(t.id);
          const a = assignments.find((x) => x.tenant_id === t.id);
          return (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={on} onChange={() => toggle(t.id)} />
                <span style={{ fontSize: 'var(--t-sm)' }}>{t.name}</span>
              </label>
              {on && (
                <FormSelect
                  value={a?.direction_override ?? ''}
                  onChange={(e) => setOverride(t.id, e.target.value)}
                  style={{ width: 150, height: 30 }}
                >
                  <option value="">כיוון הסוכן</option>
                  <option value="inbound">נכנס בלבד</option>
                  <option value="outbound">יוצא בלבד</option>
                  <option value="both">דו-כיווני</option>
                </FormSelect>
              )}
            </div>
          );
        })}
      </div>
      <button type="button" className="btn primary" onClick={save} disabled={saving}>
        {saving ? 'שומר…' : 'שמור שיוך'}
      </button>
    </div>
  );
}

const RESOLUTION_LABEL: Record<string, string> = { resolved: 'טופל', follow_up: 'דורש המשך', unresolved: 'לא טופל' };

/** ציון משנה של ה-QA; ריק כשהמדד לא הוחזר. */
function QaMetric({ label, value }: { label: string; value?: number | null }) {
  if (value == null) return null;
  const tone = value >= 80 ? 'ok' : value >= 60 ? 'warn' : 'danger';
  return (
    <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: 'var(--t-xs)' }}>
      <span style={{ color: 'var(--muted)' }}>{label}</span>
      <StatusBadge tone={tone} size="sm">{String(value)}</StatusBadge>
    </span>
  );
}

/**
 * פירוט שיחה: ציוני ה-QA, הכלים שהופעלו והתמליל המלא. עד כה השרת החזיר את
 * כולם והמסך הציג רק את הציון הכולל — כך שהסיבה לציון נמוך לא הייתה נגישה.
 */
function SessionDetails({ session }: { session: VoiceSession }) {
  const [open, setOpen] = useState(false);
  const m = session.ai_metrics;
  const tools = (session.tools_used ?? []).map((t) => typeof t === 'string' ? t : JSON.stringify(t));
  const hasDetails = !!m?.notes?.length || !!session.transcript || tools.length > 0
    || m?.correctness != null || m?.politeness != null || m?.flow_adherence != null;
  if (!hasDetails) return null;

  return (
    <div style={{ marginTop: 8 }}>
      <button type="button" className="btn ghost" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {open ? '▾ הסתר פירוט' : '▸ פירוט שיחה'}
      </button>
      {open && (
        <div className="stack" style={{ marginTop: 8, gap: 8 }}>
          {(m?.correctness != null || m?.politeness != null || m?.flow_adherence != null || m?.resolution) && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
              <QaMetric label="דיוק" value={m?.correctness} />
              <QaMetric label="נימוס" value={m?.politeness} />
              <QaMetric label="נאמנות לזרימה" value={m?.flow_adherence} />
              {m?.resolution && (
                <StatusBadge tone={m.resolution === 'resolved' ? 'ok' : m.resolution === 'follow_up' ? 'warn' : 'danger'} size="sm">
                  {RESOLUTION_LABEL[m.resolution] ?? m.resolution}
                </StatusBadge>
              )}
            </div>
          )}
          {!!m?.notes?.length && (
            <ul style={{ margin: 0, paddingInlineStart: 18, fontSize: 'var(--t-sm)' }}>
              {m.notes.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
          )}
          {tools.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {tools.map((t, i) => <StatusBadge key={i} tone="neutral" size="sm">{t}</StatusBadge>)}
            </div>
          )}
          {session.transcript && (
            <pre style={{
              margin: 0, maxHeight: 320, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              fontSize: 'var(--t-sm)', fontFamily: 'inherit', background: 'var(--surface-2)',
              padding: 8, borderRadius: 6, border: '1px solid var(--line)',
            }}>{session.transcript}</pre>
          )}
        </div>
      )}
    </div>
  );
}

function SessionsTab({ agentId }: { agentId: number }) {
  const [rows, setRows] = useState<VoiceSession[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    void superApi.voiceSessions(agentId, { per_page: 50 })
      .then((r) => setRows(r.data))
      .catch(() => toast('שגיאה בטעינת השיחות', 'danger'))
      .finally(() => setLoading(false));
  }, [agentId]);

  if (loading) return <Card>טוען…</Card>;
  if (rows.length === 0) return <EmptyState title="אין שיחות" description="הסוכן עוד לא ניהל שיחות." />;

  return (
    <div className="stack">
      {rows.map((s) => (
        <Card key={s.id}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
            <span dir="ltr" style={{ fontSize: 'var(--t-sm)' }}>{s.caller_number ?? '—'}</span>
            <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
              {s.ai_metrics?.score != null && (
                <StatusBadge
                  tone={s.ai_metrics.score >= 80 ? 'ok' : s.ai_metrics.score >= 60 ? 'warn' : 'danger'}
                  size="sm"
                >{`QA ${s.ai_metrics.score}`}</StatusBadge>
              )}
              <StatusBadge tone="neutral" size="sm">{s.disposition ?? s.status ?? '—'}</StatusBadge>
            </span>
          </div>
          <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }} dir="ltr">
            {s.started_at ?? s.created_at ?? ''}{s.billed_seconds ? ` · ${s.billed_seconds}s` : ''}
          </div>
          {s.summary && <p style={{ fontSize: 'var(--t-sm)', margin: '6px 0 0' }}>{s.summary}</p>}
          {s.recording_play_url && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <AudioPlayer src={s.recording_play_url} duration={s.billed_seconds ?? 0} />
              </div>
              <a href={s.recording_play_url} download target="_blank" rel="noreferrer" className="btn" style={{ flexShrink: 0 }}>הורד</a>
            </div>
          )}
          <SessionDetails session={s} />
        </Card>
      ))}
    </div>
  );
}

function MemoriesTab({ agentId, tenantName }: { agentId: number; tenantName: (id?: number | null) => string | null }) {
  const [rows, setRows] = useState<VoiceMemory[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    void superApi.voiceMemories(agentId, { per_page: 50 })
      .then((r) => setRows(r.data))
      .catch(() => toast('שגיאה בטעינת הזיכרון', 'danger'))
      .finally(() => setLoading(false));
  }, [agentId]);

  useEffect(() => { load(); }, [load]);

  async function forget(m: VoiceMemory) {
    try {
      await superApi.deleteVoiceMemory(agentId, m.id);
      toast('הזיכרון של המתקשר נמחק', 'ok');
      setRows((prev) => prev.filter((r) => r.id !== m.id));
    } catch {
      toast('שגיאה במחיקה', 'danger');
    }
  }

  if (loading) return <Card>טוען…</Card>;
  if (rows.length === 0) return <EmptyState title="אין זיכרון" description="אין מתקשרים שמורים לסוכן זה." />;

  return (
    <div className="stack">
      <p style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)', margin: 0 }}>
        זיכרון per-מתקשר (מידע אישי). מחיקה = "שכח מתקשר זה" (GDPR).
      </p>
      {rows.map((m) => (
        <Card key={m.id}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, alignItems: 'center' }}>
            <div>
              <strong style={{ fontSize: 'var(--t-sm)' }}>{m.customer_name ?? 'ללא שם'}</strong>
              {m.preferred_address_style && (
                <StatusBadge tone="neutral" size="sm">
                  {m.preferred_address_style === 'male' ? 'לשון זכר' : 'לשון נקבה'}
                </StatusBadge>
              )}
              <span dir="ltr" style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginInlineStart: 8 }}>{m.phone_number}</span>
            </div>
            <button type="button" className="btn danger" onClick={() => forget(m)}>שכח</button>
          </div>
          <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
            {tenantName(m.tenant_id) ?? ''}{m.call_count ? ` · ${m.call_count} שיחות` : ''}
          </div>
          {m.summary && <p style={{ fontSize: 'var(--t-sm)', margin: '6px 0 0' }}>{m.summary}</p>}
        </Card>
      ))}
    </div>
  );
}


/**
 * שדה עריכת JSON קטן עם ולידציה — לכותרות/תבנית-גוף/סכימה של פונקציית API.
 * ריק → null. JSON שגוי מסומן באדום ולא נשמר (onChange לא נקרא).
 */
function JsonField({ label, value, onChange, placeholder }: {
  label: string;
  value?: Record<string, unknown> | null;
  onChange: (v: Record<string, unknown> | null) => void;
  placeholder?: string;
}) {
  const [text, setText] = useState(value ? JSON.stringify(value, null, 0) : '');
  const [err, setErr] = useState(false);
  return (
    <div>
      <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginBottom: 2 }}>{label}</div>
      <textarea
        value={text}
        dir="ltr"
        rows={2}
        placeholder={placeholder}
        onChange={(e) => {
          const v = e.target.value;
          setText(v);
          if (v.trim() === '') { setErr(false); onChange(null); return; }
          try { const parsed = JSON.parse(v); setErr(false); onChange(parsed); }
          catch { setErr(true); }
        }}
        style={{
          width: '100%', padding: 6, fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)',
          border: `1px solid ${err ? 'var(--danger)' : 'var(--line)'}`, borderRadius: 'var(--r-2)',
          background: 'var(--surface)', resize: 'vertical',
        }}
      />
      {err && <div style={{ fontSize: 'var(--t-xs)', color: 'var(--danger)' }}>JSON לא תקין</div>}
    </div>
  );
}
